import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DocumentNode, DocumentRelationship } from '../../types/documents';
import { FolderIcon, DocumentIcon, EllipsisVerticalIcon, ChevronRightIcon, EyeIcon, ArrowDownTrayIcon, TrashIcon, KeyIcon, ShareIcon, ChatBubbleLeftRightIcon, ChatBubbleLeftIcon, MagnifyingGlassIcon, MagnifyingGlassMinusIcon, PencilIcon, PencilSquareIcon, PlusIcon, FolderPlusIcon, HandThumbUpIcon, HandThumbDownIcon, StarIcon, DocumentDuplicateIcon, ChatBubbleLeftRightIcon as CommentIcon, PaperAirplaneIcon, ArchiveBoxIcon, CheckCircleIcon, XCircleIcon, LinkIcon, ClipboardDocumentListIcon, ClockIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { DocumentCollaboration } from './DocumentCollaboration';
import { getFileTypeIcon } from './fileTypeIcons';
import DocumentNodePermissionsModal from './modals/DocumentNodePermissionsModal';
import { DocumentVersionHistoryModal } from './modals/DocumentVersionHistoryModal';
import { ShareDocumentModal } from './modals/ShareDocumentModal';
import { DocumentCommentsModal } from './DocumentCommentsModal';
import { chatbotService } from '../../services/chatbotService';
import { indexingService } from '../../services/indexingService';
import { documentStatusService } from '../../services/documentStatusService';
import { useToast } from '../UI/ToastProvider';
import { DocumentCategoriesAndTags } from './DocumentCategoriesAndTags';
import { documentService } from '../../services/documentService';
import { getCustomerIdFromStorage } from '../../hooks/useCrossTenantSelector';
import documentDownloadService from '../../services/documentDownloadService';
import { useDocumentPermissions } from '../../hooks/useDocumentPermissions';
import { useDocumentStatus } from '../../hooks/useDocumentStatus';
import { PermissionLoader } from '../UI/PermissionLoader';
import { ActionBlockedModal } from '../UI/ActionBlockedModal';
import DocumentViewer from './DocumentViewer';
import OpenDocumentViewer from './OpenDocumentViewer';
import { mergeNodeWithRenameDto } from '../../utils/documentTreeRename';
import { useAuth } from '../../contexts/AuthContext';
import { PublishPreviewModal } from './modals/PublishPreviewModal';
import { StandaloneSignatureRequestModal } from './modals/StandaloneSignatureRequestModal';
import { SignaturePlacementModal } from './modals/SignaturePlacementModal';
import { documentRelationshipService } from '../../services/documentRelationshipService';
import { RelatedDocumentsModal } from './modals/RelatedDocumentsModal';
import { AddToChatbotModal } from './modals/AddToChatbotModal';
import { RemoveFromChatbotModal } from './modals/RemoveFromChatbotModal';
import { ManageChatbotsModal } from './modals/ManageChatbotsModal';
import { workspaceService, WorkspaceForDocument } from '../../services/workspaceService';
import { useNavigate } from 'react-router-dom';
import {
  documentWorkflowService,
  type DocumentWorkflowDto,
  type DocumentWorkflowStepDto,
  type ExecuteWorkflowTransitionResult,
} from '../../services/documentWorkflowService';
import {
  documentSignatureService,
  SignatureStatusCode,
  SignatureTypeCode,
  type DocumentSignatureDto,
  type DocumentSignerDto,
  type SignaturePlacementInputDto,
} from '../../services/documentSignatureService';
import { workflowService, type Workflow, type WorkflowStep } from '../../services/workflowService';
import { WorkflowTransitionModal } from './modals/WorkflowTransitionModal';
import { DocumentSignersModal } from './modals/DocumentSignersModal';
import { WorkflowNewVersionConfirmModal } from './modals/WorkflowNewVersionConfirmModal';
import { normalizeDocumentStatus, WorkflowActionCodes, isArchiveStepType, WorkflowStepType } from '../../utils/workflowConstants';
import { actionAdvancesToSignatureStep, getNextStepAfterAction } from '../../utils/workflowActionCoherence';
import {
  mapWorkflowDefinitionTypeToUiStatus,
  shouldApplyStandaloneSignatureRules,
  workflowTemplateHasReviewPhase,
  workflowTemplateHasSignaturePhase,
} from '../../utils/workflowDocumentUi';
import {
  blocksPostPublishActionsUntilSignature,
  isSignatureQuorumSatisfied,
  resolveSignatureObligation,
} from '../../utils/signatureObligation';
import {
  buildWorkflowBannerTimelineFromTemplate,
  friendlyWorkflowStepType,
  orderWorkflowTemplateSteps,
} from '../../utils/workflowBannerTimeline';
import { getWorkflowStatusBadgeClasses, getWorkflowStatusLabel } from '../../utils/workflowStatusBadge';
import { isValidRouteDocumentId } from '../../utils/documentRouteUtils';
import {
  isCancelledSignatureRequestStatus,
  isCompletedSignatureRequestStatus,
  isInProgressSignatureRequestStatus,
  isPendingSignatureRequestStatus,
  signerStatusMeansDone,
} from '../../utils/signatureStatusUtils';
import { DocumentAuditHistoryModal } from './modals/DocumentAuditHistoryModal';
import { DocumentWorkflowHistoryModal } from './modals/DocumentWorkflowHistoryModal';
import { DocumentLifecycleScheduleModal } from './modals/DocumentLifecycleScheduleModal';
import { translateDocumentWorkflowSource } from '../../utils/workflowSourceLabels';
import type { DocumentLifecycleScheduleDto } from '../../types/documents';
import { useSearchParams } from 'react-router-dom';
import { WorkflowSignSignatureModal } from './modals/WorkflowSignSignatureModal';

function findMySignerInWorkflowRequest(
  signature: DocumentSignatureDto | null,
  userId: string | undefined,
  userEmail: string | undefined
): DocumentSignerDto | null {
  if (!signature?.signers?.length || !userId) {
    return null;
  }
  const uid = userId.trim().toLowerCase();
  const em = (userEmail || '').trim().toLowerCase();
  for (const s of signature.signers) {
    const sid = s.userId?.trim().toLowerCase();
    if (sid && sid === uid) {
      return s;
    }
    const sem = s.email?.trim().toLowerCase();
    if (!sid && em && sem === em) {
      return s;
    }
  }
  return null;
}

function findMyPendingSignerForWorkflow(
  signature: DocumentSignatureDto | null,
  userId: string | undefined,
  userEmail: string | undefined
): DocumentSignerDto | null {
  const me = findMySignerInWorkflowRequest(signature, userId, userEmail);
  if (!me || me.status !== SignatureStatusCode.Pending) {
    return null;
  }
  return me;
}

function pickInProgressSignatureRequest(
  active: DocumentSignatureDto | null,
  latest: DocumentSignatureDto | null
): DocumentSignatureDto | null {
  if (active && isInProgressSignatureRequestStatus(active.status)) {
    return active;
  }
  if (latest && isInProgressSignatureRequestStatus(latest.status)) {
    return latest;
  }
  return null;
}

function pickPendingSignatureRequest(
  active: DocumentSignatureDto | null,
  latest: DocumentSignatureDto | null
): DocumentSignatureDto | null {
  if (active && isPendingSignatureRequestStatus(active.status)) {
    return active;
  }
  if (latest && isPendingSignatureRequestStatus(latest.status)) {
    return latest;
  }
  return null;
}

function canEditSignaturePlacements(signature: DocumentSignatureDto | null): boolean {
  if (!signature?.signers?.length)
  {
    return false;
  }

  if (isCompletedSignatureRequestStatus(signature.status) || isCancelledSignatureRequestStatus(signature.status))
  {
    return false;
  }

  return !signature.signers.some((s) => s.status === SignatureStatusCode.Signed);
}

function normalizeGuidString(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  const raw = String(value).trim();
  if (!raw) {
    return null;
  }
  const t = raw.toLowerCase().replace(/-/g, '').replace(/[{}]/g, '');
  if (!t) {
    return null;
  }
  return t;
}

function documentIdsMatch(a: string | undefined | null, b: string | undefined | null): boolean {
  const na = normalizeGuidString(a);
  const nb = normalizeGuidString(b);
  if (na === null || nb === null) {
    return false;
  }
  return na === nb;
}

type SignedDocumentSource = 'final' | 'partial' | 'original';

async function resolveDocumentBlobForSignature(
  documentId: string,
  sig: DocumentSignatureDto | null | undefined
): Promise<{ blob: Blob; source: SignedDocumentSource }> {
  const matches = Boolean(sig && documentIdsMatch(sig.documentId, documentId));
  if (!matches || !sig) {
    const blob = await documentDownloadService.downloadDocument(documentId);
    return { blob, source: 'original' };
  }

  if (sig.finalSignedDocumentAvailable === true) {
    const blob = await documentSignatureService.fetchSignedFinalPdfBlob(sig.id);
    return { blob, source: 'final' };
  }

  if (sig.partialSignedDocumentAvailable === true) {
    try {
      const blob = await documentSignatureService.fetchPartialSignedPdfBlob(sig.id);
      return { blob, source: 'partial' };
    } catch {
      // fallback para o original abaixo
    }
  }

  const blob = await documentDownloadService.downloadDocument(documentId);
  return { blob, source: 'original' };
}

function signatureHasCollectedSignatures(sig: DocumentSignatureDto | null | undefined): boolean {
  if (!sig) {
    return false;
  }

  const signedCount =
    sig.signedCount ??
    sig.signers?.filter((s) => s.status === SignatureStatusCode.Signed).length ??
    0;

  return signedCount > 0;
}

async function downloadDocumentForSignature(
  documentId: string,
  fileName: string,
  sig: DocumentSignatureDto | null | undefined
): Promise<SignedDocumentSource> {
  const matches = Boolean(sig && documentIdsMatch(sig.documentId, documentId));
  if (!matches || !sig) {
    await documentDownloadService.downloadAndSaveDocument(documentId, fileName);
    return 'original';
  }

  if (sig.finalSignedDocumentAvailable === true) {
    const withoutExt = fileName.replace(/\.[^/.]+$/, '');
    await documentSignatureService.downloadSignedFinalPdf(sig.id, `${withoutExt}-assinado.pdf`);
    return 'final';
  }

  if (sig.partialSignedDocumentAvailable === true) {
    try {
      const withoutExt = fileName.replace(/\.[^/.]+$/, '');
      await documentSignatureService.downloadPartial(sig.id, `${withoutExt}-parcial.pdf`);
      return 'partial';
    } catch {
      // fallback para o original abaixo
    }
  }

  await documentDownloadService.downloadAndSaveDocument(documentId, fileName);
  return 'original';
}

function isWorkflowCapableNodeType(nodeType: string | undefined): boolean {
  return (
    nodeType === 'edoc' ||
    nodeType === 'document' ||
    nodeType === 'file' ||
    nodeType === 'pdf' ||
    nodeType === 'word'
  );
}

function isLatestSignatureRequestFullyDone(latest: DocumentSignatureDto | null): boolean {
  if (!latest?.signers?.length) {
    return true;
  }
  return isSignatureQuorumSatisfied(latest);
}

function isSignatureRequestEffectivelyCompleted(latest: DocumentSignatureDto | null): boolean {
  if (!latest) {
    return false;
  }
  if (isCancelledSignatureRequestStatus(latest.status)) {
    return false;
  }
  if (isPendingSignatureRequestStatus(latest.status)) {
    return false;
  }
  const completedAt = latest.completedAt;
  if (typeof completedAt === 'string' && completedAt.trim().length > 0) {
    return true;
  }
  if (isCompletedSignatureRequestStatus(latest.status)) {
    return true;
  }
  if (
    isInProgressSignatureRequestStatus(latest.status) &&
    latest.signers &&
    latest.signers.length > 0 &&
    isLatestSignatureRequestFullyDone(latest)
  ) {
    return true;
  }
  return false;
}

function isSignatureCompletedForPublishedContent(
  latest: DocumentSignatureDto | null,
  publishedVersionId: string | null | undefined,
  currentVersionId: string | null | undefined
): boolean {
  if (!latest || !isSignatureRequestEffectivelyCompleted(latest)) {
    return false;
  }
  if (!isLatestSignatureRequestFullyDone(latest)) {
    return false;
  }
  const sig = normalizeGuidString(latest.documentVersionId);
  if (!sig) {
    return true;
  }
  const pub = normalizeGuidString(publishedVersionId);
  const cur = normalizeGuidString(currentVersionId);
  if (pub && sig === pub) {
    return true;
  }
  if (cur && sig === cur) {
    return true;
  }
  if (!pub && !cur) {
    return true;
  }
  if (pub && cur && pub === cur && sig !== pub) {
    return false;
  }
  if (pub && !cur && sig !== pub) {
    return false;
  }
  if (!pub && cur && sig !== cur) {
    return false;
  }
  return true;
}

function formatLifecycleDateHint(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatPublicationExpiration(iso: string | null): string {
  if (!iso) {
    return '—';
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return date.toLocaleString('pt-BR');
}

interface DocumentContentProps {
  selectedNode: DocumentNode | null;
  contextTenantId?: string;
  onNodeSelect: (node: DocumentNode) => void;
  onNewDocument: () => void;
  onNewFolder: () => void;
  onOpenDocument: (node: DocumentNode) => void;
  onDeleteDocument: (node: DocumentNode) => void;
  onDocumentUpdated: (documentId: string, newPathAfterRename?: string) => void;
  onEditDocument: (node: DocumentNode) => void;
  onDuplicateDocument?: (documentId: string) => Promise<void>;
  onPublishDocument?: (
    node: DocumentNode,
    options?: { publicationExpiresAt?: string | null }
  ) => Promise<void>;
}

const LEGACY_REMOVE_WORKSPACE_ID = '00000000-0000-0000-0000-000000000000';

const DocumentContent: React.FC<DocumentContentProps> = ({
  selectedNode,
  contextTenantId,
  onNodeSelect,
  onNewDocument,
  onNewFolder,
  onOpenDocument,
  onDeleteDocument,
  onDocumentUpdated,
  onEditDocument,
  onDuplicateDocument,
  onPublishDocument,
}) => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [showOpenDocumentViewer, setShowOpenDocumentViewer] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [showDocumentWorkflowHistoryModal, setShowDocumentWorkflowHistoryModal] = useState(false);
  const [showDocumentAuditHistoryModal, setShowDocumentAuditHistoryModal] = useState(false);
  const [viewContent, setViewContent] = useState<string>('');
  const [chatbotLoadingDocuments, setChatbotLoadingDocuments] = useState<Set<string>>(() => new Set());
  const [showAddToChatbotModal, setShowAddToChatbotModal] = useState(false);
  const [showRemoveFromChatbotModal, setShowRemoveFromChatbotModal] = useState(false);
  const [showManageChatbotsModal, setShowManageChatbotsModal] = useState(false);
  const [linkedChatbots, setLinkedChatbots] = useState<WorkspaceForDocument[]>([]);
  const [linkedChatbotsLoading, setLinkedChatbotsLoading] = useState(false);
  const selectedNodeIdRef = useRef<string | undefined>(selectedNode?.id);
  const pendingChatbotActionsRef = useRef<Map<string, { action: 'add' | 'remove'; indexedWorkspaceIds: string[] }>>(new Map());
  const [isLoadingIndexing, setIsLoadingIndexing] = useState(false);
  const [documentCategory, setDocumentCategory] = useState<string>('');
  const [documentCategories, setDocumentCategories] = useState<string[]>([]);
  const [documentTags, setDocumentTags] = useState<string[]>([]);
  const [documentDescription, setDocumentDescription] = useState<string>('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [descriptionTimeout, setDescriptionTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isLoadingBreadcrumb, setIsLoadingBreadcrumb] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [isLoading, setIsLoading] = useState(false);
  const [cleanupFunction, setCleanupFunction] = useState<(() => void) | null>(null);
  
  // Estados para modais de ação bloqueada
  const [showActionBlockedModal, setShowActionBlockedModal] = useState(false);
  const [blockedActionName, setBlockedActionName] = useState<string>('');
  
  // Estados para avaliação de documentos
  const [documentRatings, setDocumentRatings] = useState<{[key: string]: 'like' | 'dislike' | null}>({});
  const [evaluatingDocuments, setEvaluatingDocuments] = useState<Set<string>>(new Set());
  
  // Estados para favoritos
  const [documentFavorites, setDocumentFavorites] = useState<{[key: string]: boolean}>({});
  const [favoritingDocuments, setFavoritingDocuments] = useState<Set<string>>(new Set());
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [duplicateSubmitting, setDuplicateSubmitting] = useState(false);
  const [downloadingChildId, setDownloadingChildId] = useState<string | null>(null);
  const [showPublishPreview, setShowPublishPreview] = useState(false);
  const [showStandaloneSignatureRequestModal, setShowStandaloneSignatureRequestModal] = useState(false);
  const [pendingSignaturePlacementModal, setPendingSignaturePlacementModal] = useState<{
    signatureRequestId: string;
    /** `standalone` ou código da ação de workflow a executar após posicionar */
    action: string;
    comment?: string;
  } | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publicationExpiresAt, setPublicationExpiresAt] = useState<string | null>(null);

  const [documentWorkflow, setDocumentWorkflow] = useState<DocumentWorkflowDto | null>(null);
  const [workflowTemplate, setWorkflowTemplate] = useState<Workflow | null>(null);
  const [workflowUiLoading, setWorkflowUiLoading] = useState(false);
  const [workflowTransitionModal, setWorkflowTransitionModal] = useState<{
    action: string;
    requiresComment: boolean;
  } | null>(null);
  const [workflowSignModalSigner, setWorkflowSignModalSigner] = useState<DocumentSignerDto | null>(null);
  const [activeSignatureRequest, setActiveSignatureRequest] = useState<DocumentSignatureDto | null>(null);
  const [latestSignatureRequest, setLatestSignatureRequest] = useState<DocumentSignatureDto | null>(null);
  const [documentPublishedVersionId, setDocumentPublishedVersionId] = useState<string | null>(null);
  const [documentCurrentVersionId, setDocumentCurrentVersionId] = useState<string | null>(null);
  const [showSignersModal, setShowSignersModal] = useState(false);
  const [workflowNewVersionModal, setWorkflowNewVersionModal] = useState<'published' | 'archived' | null>(null);
  const [workflowStatusOverride, setWorkflowStatusOverride] = useState<string | null>(null);
  const [showLifecycleScheduleModal, setShowLifecycleScheduleModal] = useState(false);
  const [lifecycleSchedule, setLifecycleSchedule] = useState<{
    publish: string | null;
    archive: string | null;
  }>({ publish: null, archive: null });

  const [relatedDocuments, setRelatedDocuments] = useState<DocumentRelationship[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [showRelatedModal, setShowRelatedModal] = useState(false);

  const { addToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signatureInviteToken = searchParams.get('inviteToken');

  // Hook para gerenciar permissões do documento
  const { permissions, isLoading: permissionsLoading } = useDocumentPermissions(selectedNode?.id || null);

  // Hook para gerenciar status do documento
  const { 
    status: documentStatus, 
    isLoading: statusLoading, 
    error: statusError,
    refreshStatus
  } = useDocumentStatus(selectedNode?.id || null);

  const statusForNode = useCallback(
    (node: DocumentNode | null | undefined): string => {
      if (selectedNode?.id && node?.id === selectedNode.id && workflowStatusOverride) {
        return normalizeDocumentStatus(workflowStatusOverride) as string;
      }
      return normalizeDocumentStatus(node?.status) as string;
    },
    [selectedNode?.id, workflowStatusOverride]
  );

  const loadActiveWorkflow = useCallback(async (documentId: string) => {
    setWorkflowUiLoading(true);
    setDocumentWorkflow(null);
    setWorkflowTemplate(null);
    try {
      const dw = await documentWorkflowService.getDocumentWorkflow(documentId);
      setDocumentWorkflow(dw);
      if (dw?.workflowId) {
        try {
          const tpl = await workflowService.getWorkflow(dw.workflowId);
          if (tpl.steps?.length) {
            setWorkflowTemplate(tpl);
          } else {
            try {
              const steps = await workflowService.getWorkflowStepsForWorkflow(dw.workflowId);
              setWorkflowTemplate({ ...tpl, steps });
            } catch {
              setWorkflowTemplate(tpl);
            }
          }
        } catch (e) {
          setWorkflowTemplate(null);
          const msg =
            e instanceof Error ? e.message : 'Não foi possível carregar o modelo do workflow.';
          addToast(msg, 'error');
        }
      } else {
        setWorkflowTemplate(null);
      }
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : 'Não foi possível carregar o workflow ativo. Verifique a API (ex.: comentário de etapa, permissão).';
      addToast(msg, 'error');
      setDocumentWorkflow(null);
      setWorkflowTemplate(null);
    } finally {
      setWorkflowUiLoading(false);
    }
  }, [addToast]);

  const refreshDocumentSignatureState = useCallback(async (documentId: string) => {
    if (!isValidRouteDocumentId(documentId)) {
      return;
    }
    const [active, latest] = await Promise.all([
      documentSignatureService.getActiveByDocument(documentId).catch(() => null),
      documentSignatureService.getLatestByDocument(documentId).catch(() => null),
    ]);
    setActiveSignatureRequest(active);
    setLatestSignatureRequest(latest);
    try {
      const doc = await documentService.getDocument(documentId);
      if (doc) {
        setDocumentPublishedVersionId(doc.currentPublishedVersionId ?? null);
        setDocumentCurrentVersionId(doc.currentVersionId ?? null);
      }
    } catch {
      /* mantém ids já carregados */
    }
  }, []);

  useEffect(() => {
    setWorkflowStatusOverride(null);
    setDocumentWorkflow(null);
    setWorkflowTemplate(null);
    setActiveSignatureRequest(null);
    setLatestSignatureRequest(null);
    setDocumentPublishedVersionId(null);
    setDocumentCurrentVersionId(null);
    setShowSignersModal(false);
    setShowStandaloneSignatureRequestModal(false);
    setWorkflowSignModalSigner(null);
  }, [selectedNode?.id]);

  useEffect(() => {
    setWorkflowTransitionModal(null);
    setWorkflowNewVersionModal(null);
    setWorkflowSignModalSigner(null);
    if (!selectedNode?.id || selectedNode.id === 'root') return;
    if (selectedNode.nodeType === 'folder') return;
    if (!isWorkflowCapableNodeType(selectedNode.nodeType)) return;
    void loadActiveWorkflow(selectedNode.id);
  }, [selectedNode?.id, selectedNode?.nodeType, loadActiveWorkflow]);

  useEffect(() => {
    if (!selectedNode?.id || selectedNode.id === 'root') {
      return;
    }
    if (selectedNode.nodeType === 'folder') {
      return;
    }
    if (!isWorkflowCapableNodeType(selectedNode.nodeType)) {
      return;
    }
    if (!isValidRouteDocumentId(selectedNode.id)) {
      return;
    }
    void refreshDocumentSignatureState(selectedNode.id);
  }, [selectedNode?.id, selectedNode?.nodeType, refreshDocumentSignatureState]);

  useEffect(() => {
    const handler = (ev: Event) => {
      const ce = ev as CustomEvent<{ documentId?: string }>;
      const docId = ce.detail?.documentId;
      if (!docId || !selectedNode?.id || selectedNode.id === 'root') {
        return;
      }
      if (selectedNode.nodeType === 'folder') {
        return;
      }
      if (!documentIdsMatch(docId, selectedNode.id)) {
        return;
      }
      if (!isValidRouteDocumentId(selectedNode.id)) {
        return;
      }
      void refreshDocumentSignatureState(selectedNode.id);
    };
    window.addEventListener('signed-final-pdf-notification', handler as EventListener);
    return () => {
      window.removeEventListener('signed-final-pdf-notification', handler as EventListener);
    };
  }, [selectedNode?.id, selectedNode?.nodeType, refreshDocumentSignatureState]);

  useEffect(() => {
    if (!selectedNode?.id || selectedNode.id === 'root') {
      setLifecycleSchedule({ publish: null, archive: null });
      setPublicationExpiresAt(null);
      setDocumentPublishedVersionId(null);
      setDocumentCurrentVersionId(null);
      return;
    }
    if (selectedNode.nodeType === 'folder') {
      setLifecycleSchedule({ publish: null, archive: null });
      setPublicationExpiresAt(null);
      setDocumentPublishedVersionId(null);
      setDocumentCurrentVersionId(null);
      return;
    }
    const t = selectedNode.nodeType;
    if (!isWorkflowCapableNodeType(t) && t !== 'link') return;
    let cancelled = false;
    void documentService
      .getDocument(selectedNode.id)
      .then(doc => {
        if (cancelled || !doc) return;
        setLifecycleSchedule({
          publish: doc.scheduledPublishDate ?? null,
          archive: doc.scheduledArchiveDate ?? null,
        });
        setPublicationExpiresAt(doc.publicationExpiresAt ?? null);
        setDocumentPublishedVersionId(doc.currentPublishedVersionId ?? null);
        setDocumentCurrentVersionId(doc.currentVersionId ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setLifecycleSchedule({ publish: null, archive: null });
          setPublicationExpiresAt(null);
          setDocumentPublishedVersionId(null);
          setDocumentCurrentVersionId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedNode?.id, selectedNode?.nodeType]);

  const handleWorkflowTransitionSuccess = useCallback(
    async (result: ExecuteWorkflowTransitionResult) => {
      const clonedId = result.newDocumentId?.trim();
      if (clonedId) {
        navigate(`/documents/${clonedId}/details`, { replace: true });
        return;
      }

      if (!selectedNode?.id) {
        return;
      }
      if (result.newStatus) {
        setWorkflowStatusOverride(result.newStatus);
        onNodeSelect({ ...selectedNode, status: result.newStatus });
      }
      await loadActiveWorkflow(selectedNode.id);
      
      await refreshDocumentSignatureState(selectedNode.id);
      try {
        const doc = await documentService.getDocument(selectedNode.id);
        if (doc) {
          if (doc.status) {
            setWorkflowStatusOverride(doc.status);
          }
          onNodeSelect({
            ...selectedNode,
            status: doc.status || selectedNode.status,
            currentVersion: doc.currentVersion ?? selectedNode.currentVersion,
            ...(doc.metadata
              ? { metadata: { ...(selectedNode.metadata ?? {}), ...doc.metadata } }
              : {}),
          });
          setLifecycleSchedule({
            publish: doc.scheduledPublishDate ?? null,
            archive: doc.scheduledArchiveDate ?? null,
          });
          setPublicationExpiresAt(doc.publicationExpiresAt ?? null);
          setDocumentPublishedVersionId(doc.currentPublishedVersionId ?? null);
          setDocumentCurrentVersionId(doc.currentVersionId ?? null);
        }
      } catch {
        /* mantém o status vindo do transition + override já aplicado */
      }
      await refreshStatus();
      onDocumentUpdated(selectedNode.id);
    },
    [selectedNode, loadActiveWorkflow, refreshDocumentSignatureState, refreshStatus, onDocumentUpdated, onNodeSelect, navigate]
  );

  const isPublicationExpired = useMemo((): boolean => {
    const status = statusForNode(selectedNode);
    if (status !== 'published') {
      return false;
    }

    if (!publicationExpiresAt) {
      return false;
    }

    const expirationDate = new Date(publicationExpiresAt);
    if (Number.isNaN(expirationDate.getTime())) {
      return false;
    }

    return expirationDate.getTime() < Date.now();
  }, [publicationExpiresAt, selectedNode, statusForNode]);

  const isWorkflowStepClosed = (status: string | undefined): boolean => {
    const s = (status || '').toLowerCase();
    return s === 'completed' || s === 'rejected' || s === 'cancelled';
  };

  const currentStepRuntime = useMemo(() => {
    if (!documentWorkflow?.steps?.length) return null;
    const sorted = [...documentWorkflow.steps].sort((a, b) => a.stepOrder - b.stepOrder);
    const open = sorted.find(s => !isWorkflowStepClosed(s.status));
    const reference = (open ?? sorted[sorted.length - 1]) as DocumentWorkflowStepDto | undefined;
    if (!reference) return null;
    const hasOpenStep = open != null;
    const d = reference;
    const tpl = workflowTemplate?.steps?.find(ws => ws.id === reference.workflowStepId) ?? null;
    const orderedSteps = [...(workflowTemplate?.steps ?? [])].sort((a, b) => a.order - b.order);
    const nextFromTemplate =
      tpl != null ? orderedSteps.find(s => s.order > tpl.order) ?? null : null;
    const nextFromSnapshot: WorkflowStep | null =
      nextFromTemplate ??
      (() => {
        const nextD = sorted.find(
          s =>
            (s as DocumentWorkflowStepDto).definitionOrder != null &&
            ((s as DocumentWorkflowStepDto).definitionOrder as number) > (d.definitionOrder ?? 0)
        ) as DocumentWorkflowStepDto | undefined;
        if (!nextD?.definitionType) return null;
        const stub: WorkflowStep = {
          id: nextD.workflowStepId,
          workflowId: documentWorkflow.workflowId,
          name: '',
          description: '',
          type: nextD.definitionType,
          order: nextD.definitionOrder ?? 0,
          sendEmailNotification: true,
          requiresComment: nextD.definitionRequiresComment ?? false,
          approvalRule: 'Any',
          minApprovals: 1,
          allowedActions: nextD.definitionAllowedActions ?? [],
          assignees: [],
        };
        return stub;
      })();
    const fromTpl = (tpl?.allowedActions ?? []).map(a => (typeof a === 'string' ? a.trim() : '')).filter(Boolean);
    const fromDef = (d.definitionAllowedActions ?? [])
      .map(a => (typeof a === 'string' ? a.trim() : ''))
      .filter(Boolean);
    const allowedActions = Array.from(
      new Map([...fromDef, ...fromTpl].map((a) => [a.toLowerCase(), a])).values()
    );
    const definitionTypeForActions = (tpl?.type || d.definitionType || '').trim();
    const persistedForArchiveHint = normalizeDocumentStatus(selectedNode?.status ?? '') as string;
    if (
      hasOpenStep &&
      persistedForArchiveHint === 'published' &&
      isArchiveStepType(definitionTypeForActions) &&
      !allowedActions.some((a) => a.toLowerCase() === 'archive')
    ) {
      allowedActions.push('Archive');
    }
    return {
      documentStep: reference,
      hasOpenStep,
      templateStep: tpl,
      nextTemplateStep: nextFromSnapshot,
      allowedActions,
      requiresComment: tpl != null ? tpl.requiresComment === true : d.definitionRequiresComment === true,
    };
  }, [documentWorkflow, workflowTemplate, selectedNode?.status]);

  const applyStandaloneSignatureRules = useMemo(
    () =>
      shouldApplyStandaloneSignatureRules({
        hasDocumentWorkflow: !!documentWorkflow,
        templateSteps: workflowTemplate?.steps,
        instanceSteps: documentWorkflow?.steps,
      }),
    [documentWorkflow, workflowTemplate?.steps]
  );

  const standaloneSignatureExpiresAt = useMemo((): string | null => {
    if (!applyStandaloneSignatureRules)
    {
      return null;
    }

    const sig = activeSignatureRequest ?? latestSignatureRequest;
    return sig?.expiresAt ?? null;
  }, [applyStandaloneSignatureRules, activeSignatureRequest, latestSignatureRequest]);

  const isSignatureDeadlineExpired = useMemo((): boolean => {
    if (!standaloneSignatureExpiresAt)
    {
      return false;
    }

    const expirationDate = new Date(standaloneSignatureExpiresAt);
    if (Number.isNaN(expirationDate.getTime()))
    {
      return false;
    }

    return expirationDate.getTime() < Date.now();
  }, [standaloneSignatureExpiresAt]);

  const signatureObligation = useMemo(
    () =>
      resolveSignatureObligation({
        requiresSignature: selectedNode?.requiresSignature === true,
        applyStandaloneSignatureRules,
        activeSignature: activeSignatureRequest,
        latestSignature: latestSignatureRequest,
        publishedVersionId: documentPublishedVersionId,
        currentVersionId: documentCurrentVersionId,
      }),
    [
      selectedNode?.requiresSignature,
      applyStandaloneSignatureRules,
      activeSignatureRequest,
      latestSignatureRequest,
      documentPublishedVersionId,
      documentCurrentVersionId,
    ]
  );

  const postPublishBlockedBySignature = blocksPostPublishActionsUntilSignature(signatureObligation);

  const workflowContextBanner = useMemo(() => {
    if (!documentWorkflow)
      return null;

    if (workflowUiLoading)
      return { mode: 'loading' as const };
    
    if (!currentStepRuntime?.documentStep)
      return { mode: 'unknown' as const };
    
    const documentWorkflowStatus = (documentWorkflow.status || '').toLowerCase();
    const sortedSteps = [...documentWorkflow.steps].sort((a, b) => a.stepOrder - b.stepOrder);
    const openStepsIndexes = sortedSteps.findIndex(s => !isWorkflowStepClosed(s.status));
    const totalSteps = sortedSteps.length;
    const allInstancesClosed = totalSteps > 0 && sortedSteps.every(s => isWorkflowStepClosed(s.status));
    const processEndedVisually = allInstancesClosed || documentWorkflowStatus === 'completed' || documentWorkflowStatus === 'cancelled';
    const stepPosition = openStepsIndexes >= 0 ? `Etapa ${openStepsIndexes + 1} de ${totalSteps}` : `Fluxo (${totalSteps} etapas)`;
    const templateStep = currentStepRuntime.templateStep;
    const documentStep = currentStepRuntime.documentStep as DocumentWorkflowStepDto;
    const definitionType = documentStep.definitionType;
    const persistedDocumentStatus = normalizeDocumentStatus(selectedNode?.status ?? '') as string;

    let title;
    if (processEndedVisually) {
      if (documentWorkflowStatus === 'cancelled') {
        title = 'Fluxo cancelado';
      } else if (persistedDocumentStatus === 'archived') {
        title = 'Documento arquivado';
      } else {
        title = 'Fluxo concluído';
      }
    } else {
      title = templateStep?.name?.trim() || documentStep?.definitionName?.trim() || friendlyWorkflowStepType(definitionType) || 'Etapa atual';
    }

    const rawDesc = templateStep?.description?.trim() || documentStep.definitionDescription?.trim() || '';

    let description;
    if (processEndedVisually) {
      if (documentWorkflowStatus === 'cancelled') {
        description = 'Este fluxo foi cancelado. Consulte o histórico para detalhes e auditoria.';
      } else if (persistedDocumentStatus === 'archived') {
        description =
          'O documento está arquivado. Use Nova versão ou Reativar para voltar a editar, conforme as permissões. Consulte o histórico do fluxo para datas e auditoria.';
      } else {
        description = 'Todas as etapas foram concluídas. Consulte o histórico para datas, responsáveis e auditoria.';
      }
    } else if (isArchiveStepType(definitionType)) {
      description = 'Selecione uma das ações abaixo para arquivar o documento ou iniciar uma nova versão.';
    } else {
      description = rawDesc || 'Esta etapa não possui descrição cadastrada no modelo do fluxo. Use as ações abaixo conforme as regras do processo.';
    }
    
    const due = processEndedVisually || !documentStep.dueDate 
      ? null 
      : new Date(documentStep.dueDate).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

    const orderedTemplateSteps = orderWorkflowTemplateSteps(workflowTemplate?.steps);
    const { timelineSteps, currentStepIndex } = buildWorkflowBannerTimelineFromTemplate(
      orderedTemplateSteps,
      documentStep,
      sortedSteps
    );
    const markTimelineAllDone = allInstancesClosed || documentWorkflowStatus === 'completed';
    const timelineStepsFinal = markTimelineAllDone
      ? timelineSteps.map(t => ({ ...t, state: 'done' as const }))
      : timelineSteps;
    const isCurrentStepArchive = !processEndedVisually && isArchiveStepType(definitionType);
    const stepPositionResolved = markTimelineAllDone
      ? `${timelineStepsFinal.length} etapa(s) no processo`
      : isCurrentStepArchive
        ? null
        : timelineSteps.length > 0
          ? `Etapa ${currentStepIndex + 1} de ${timelineSteps.length}`
          : stepPosition;

    return {
      mode: 'active' as const,
      isEndedOverview: processEndedVisually,
      stepPosition: stepPositionResolved,
      title,
      description,
      due,
      timelineSteps: timelineStepsFinal,
    };
  }, [documentWorkflow, workflowUiLoading, currentStepRuntime, workflowTemplate, selectedNode?.status]);

  const isUserResponsibleForTemplateStep = useCallback(
    (step: WorkflowStep | null | undefined): boolean => {
      if (!user)
        return false;
      
      if (user.isAdmin)
        return true;

      const effective = (step?.assignees ?? []).filter(a =>
          (a.userId && String(a.userId).trim().length > 0) ||
          (a.teamId && String(a.teamId).trim().length > 0)
      );

      if (effective.length === 0)
        return true;

      const uid = (user.id || '').trim().toLowerCase();
      const teamIds = new Set((user.teams ?? []).map(t => String(t).trim().toLowerCase()));

      return effective.some(a => {
        const aUid = a.userId?.trim().toLowerCase();
        const aTid = a.teamId?.trim().toLowerCase();

        if (aUid && aUid === uid)
          return true;
        
        if (aTid && teamIds.has(aTid))
          return true;

        return false;
      });
    },
    [user]
  );

  const hasWorkflowActionPermission = permissions.canActions || permissions.canEdit;

  const workflowActionAllowed = (name: string): boolean => {
    const n = name.toLowerCase();
    return (
      currentStepRuntime?.allowedActions.some(a => (a || '').toLowerCase() === n) ?? false
    );
  };

  const workflowActionDisabledReason = useCallback(
    (name: string): string | null => {
      const found = documentWorkflow?.disabledActions?.find(
        d => (d.action || '').toLowerCase() === name.toLowerCase()
      );
      if (!found?.reason?.trim()) {
        return null;
      }
      return found.reason.trim();
    },
    [documentWorkflow?.disabledActions]
  );

  const shouldShowCoherenceBlockedWorkflowAction = (
    action: string,
    contextOk: boolean
  ): boolean => {
    if (!contextOk || !workflowActionAllowed(action)) {
      return false;
    }
    return !!workflowActionDisabledReason(action);
  };

  const workflowActionButtonClass = (enabled: boolean): string =>
    enabled
      ? 'inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'
      : 'inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium opacity-50 cursor-not-allowed';

  const currentWorkflowUiStatus = (node: DocumentNode): string => {
    const fromCurrentType = mapWorkflowDefinitionTypeToUiStatus(
      currentDefinitionType(),
      selectedNode?.status
    );
    if (fromCurrentType) return fromCurrentType;
    return statusForNode(node);
  };

  // Função para verificar se é um documento eletrônico
  const isElectronicDocument = (node: DocumentNode): boolean => {
    return node.nodeType === 'edoc';
  };

  const canRenameNode = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root')
      return false;
    
    if (isElectronicDocument(node))
      return permissions.canEdit;
    
    if (node.nodeType === 'folder')
      return permissions.canEdit || permissions.canCreate;

    return permissions.canEdit;
  };

  const canShareDocument = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root')
      return false;
    
    if (node.nodeType === 'folder')
      return false;

    if (statusForNode(node) !== 'published')
      return false;

    return permissions.canView;
  };

  const canDuplicateDocument = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root')
      return false;
    
    return isElectronicDocument(node) && permissions.canEdit;
  };

  const handleDuplicate = async () => {
    if (!selectedNode || !onDuplicateDocument || duplicateSubmitting)
      return;
    
    if (!canDuplicateDocument(selectedNode))
      return;
    
    setDuplicateSubmitting(true);

    try {
      await onDuplicateDocument(selectedNode.id);
    } catch {
      addToast('Não foi possível duplicar o documento.', 'error');
    } finally {
      setDuplicateSubmitting(false);
    }
  };

  const canStandaloneSendForSignature = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root')
    {
      return false;
    }

    if (!isWorkflowCapableNode(node))
    {
      return false;
    }

    if (!applyStandaloneSignatureRules)
    {
      return false;
    }

    if (documentWorkflow && workflowUiLoading)
    {
      return false;
    }

    if (statusForNode(node) !== 'published')
    {
      return false;
    }

    if (!permissions.canEdit)
    {
      return false;
    }

    if (!pickPendingSignatureRequest(activeSignatureRequest, latestSignatureRequest))
    {
      return false;
    }

    return true;
  };

  const canStandaloneOpenSignatureRequestModal = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root')
    {
      return false;
    }

    if (!isWorkflowCapableNode(node))
    {
      return false;
    }

    if (!applyStandaloneSignatureRules)
    {
      return false;
    }

    if (documentWorkflow && workflowUiLoading)
    {
      return false;
    }

    if (statusForNode(node) !== 'published')
    {
      return false;
    }

    if (!permissions.canEdit)
    {
      return false;
    }

    if (activeSignatureRequest)
    {
      return false;
    }

    if (isSignatureCompletedForPublishedContent(
      latestSignatureRequest,
      documentPublishedVersionId,
      documentCurrentVersionId
    ))
    {
      return false;
    }

    return true;
  };

  const isPublishBlockedBySignature = (_node: DocumentNode | null | undefined): boolean => {
    return false;
  };

  /** Publicação com pré-visualização (sem workflow ativo no documento). */
  const canLegacyPublishPreview = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root')
    {
      return false;
    }
    
    if (node.nodeType === 'folder')
    {
      return false;
    }
    
    if (!permissions.canEdit)
    {
      return false;
    }
    
    return statusForNode(node) !== 'published';
  };

  /** Documento criado/gerido sem workflow ativo: arquivamento via API dedicada (não confundir com etapa Arquivamento do fluxo). */
  const canArchivePublishedOutsideWorkflow = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root') {
      return false;
    }

    if (node.nodeType === 'folder') {
      return false;
    }

    if (!isWorkflowCapableNode(node)) {
      return false;
    }

    if (documentWorkflow) {
      const workflowHasArchiveStep =
        (workflowTemplate?.steps ?? []).some(s => isArchiveStepType(s.type)) ||
        (documentWorkflow.steps ?? []).some(s => isArchiveStepType(s.definitionType));

      if (workflowHasArchiveStep) {
        return false;
      }

      if (!(applyStandaloneSignatureRules && node.requiresSignature)) {
        return false;
      }
    }

    if (workflowUiLoading) {
      return false;
    }

    if (statusForNode(node) !== 'published') {
      return false;
    }

    if (!permissions.canEdit) {
      return false;
    }

    if (postPublishBlockedBySignature) {
      return false;
    }

    return true;
  };

  const handleConfirmPublish = async (nextPublicationExpiresAt: string | null) => {
    if (!selectedNode || !onPublishDocument || isPublishing)
    {
      return;
    }

    setIsPublishing(true);

    try {
      await onPublishDocument(selectedNode, { publicationExpiresAt: nextPublicationExpiresAt });
      setShowPublishPreview(false);
      setPublicationExpiresAt(nextPublicationExpiresAt);
      await refreshStatus();
      if (selectedNode.id) {
        void refreshDocumentSignatureState(selectedNode.id);
        try {
          const doc = await documentService.getDocument(selectedNode.id);
          if (doc) {
            setDocumentPublishedVersionId(doc.currentPublishedVersionId ?? null);
            setDocumentCurrentVersionId(doc.currentVersionId ?? null);
            setPublicationExpiresAt(doc.publicationExpiresAt ?? null);
          }
        } catch {
          /* mantém validade/versionamento já aplicados */
        }
      }

      addToast('Documento publicado com sucesso!', 'success');
    } catch {
      addToast('Erro ao publicar documento.', 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const isWorkflowCapableNode = (node: DocumentNode): boolean => {
    return isWorkflowCapableNodeType(node.nodeType);
  };

  const documentSignatureBanner = useMemo((): null | {
    source: 'standalone' | 'workflow';
    kind: 'completed' | 'pending_send' | 'in_progress' | 'cancelled';
    signed?: number;
    total?: number;
  } => {
    if (!selectedNode) {
      return null;
    }
    if (!isWorkflowCapableNode(selectedNode)) {
      return null;
    }
    if (!permissions.canView) {
      return null;
    }

    const hasSigData = !!(activeSignatureRequest || latestSignatureRequest);
    if (!hasSigData) {
      return null;
    }

    const buildKinds = (source: 'standalone' | 'workflow') => {
      if (pickPendingSignatureRequest(activeSignatureRequest, latestSignatureRequest)) {
        return { source, kind: 'pending_send' as const };
      }

      const standaloneCompletedPublished =
        source === 'standalone' &&
        statusForNode(selectedNode) === 'published' &&
        isSignatureCompletedForPublishedContent(
          latestSignatureRequest,
          documentPublishedVersionId,
          documentCurrentVersionId
        );

      const inProgressSig = pickInProgressSignatureRequest(activeSignatureRequest, latestSignatureRequest);
      if (inProgressSig && !standaloneCompletedPublished) {
        const total = inProgressSig.signers?.length ?? 0;
        const signed =
          inProgressSig.signers?.filter((s) => s.status === SignatureStatusCode.Signed).length ?? 0;
        return { source, kind: 'in_progress' as const, signed, total };
      }

      if (source === 'standalone') {
        if (statusForNode(selectedNode) !== 'published') {
          return null;
        }
        if (isSignatureCompletedForPublishedContent(
          latestSignatureRequest,
          documentPublishedVersionId,
          documentCurrentVersionId
        )) {
          return { source, kind: 'completed' as const };
        }
      } else {
        if (isSignatureRequestEffectivelyCompleted(latestSignatureRequest) && isLatestSignatureRequestFullyDone(latestSignatureRequest)) {
          return { source, kind: 'completed' as const };
        }
      }

      if (latestSignatureRequest && isCancelledSignatureRequestStatus(latestSignatureRequest.status)) {
        return { source, kind: 'cancelled' as const };
      }

      return null;
    };

    return buildKinds(applyStandaloneSignatureRules ? 'standalone' : 'workflow');
  }, [
    selectedNode,
    applyStandaloneSignatureRules,
    permissions.canView,
    statusForNode,
    latestSignatureRequest,
    documentPublishedVersionId,
    documentCurrentVersionId,
    activeSignatureRequest,
  ]);

  const primaryDownloadButtonLabel = useMemo(() => {
    if (!selectedNode || selectedNode.nodeType === 'folder') {
      return 'Baixar';
    }
    const sig = latestSignatureRequest;
    if (
      sig &&
      documentIdsMatch(sig.documentId, selectedNode.id) &&
      sig.finalSignedDocumentAvailable === true &&
      isSignatureRequestEffectivelyCompleted(sig)
    ) {
      return 'Baixar';
    }
    if (sig && documentIdsMatch(sig.documentId, selectedNode.id) && sig.finalSignedDocumentProcessing === true) {
      return 'Baixar';
    }
    return 'Baixar';
  }, [selectedNode, latestSignatureRequest]);

  const canShowWorkflowActions = (node: DocumentNode): boolean => {
    if (node.nodeType === 'folder')
      return false;
    
    if (!isWorkflowCapableNode(node))
      return false;
    
    if (workflowUiLoading && !documentWorkflow)
      return false;
    
    if (!documentWorkflow)
      return false;
    
    return permissions.canActions || permissions.canEdit;
  };

  const canSendToReview = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (status !== 'edit')
      return false;
    
    if (!workflowActionAllowed('SendForReview'))
      return false;

    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const currentDefinitionType = (): string =>
    (currentStepRuntime?.templateStep?.type || (currentStepRuntime?.documentStep as DocumentWorkflowStepDto | undefined)?.definitionType || '').trim();

  const nextTemplateStepIsSignature = useMemo(() => {
    const nextType = (currentStepRuntime?.nextTemplateStep?.type || '').trim();
    if (!nextType) {
      return false;
    }
    return nextType.localeCompare('Signature', undefined, { sensitivity: 'accent' }) === 0;
  }, [currentStepRuntime?.nextTemplateStep?.type]);

  const isHandoffToSignatureHop = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep) {
      return false;
    }
    if (isCurrentStepPublishDefinition()) {
      return false;
    }
    const status = currentWorkflowUiStatus(node);
    if (status !== 'review' && status !== 'edit') {
      return false;
    }
    return nextTemplateStepIsSignature;
  };

  const canSendForSignature = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep) {
      return false;
    }
    if (!workflowActionAllowed('SendForSignature')) {
      return false;
    }
    if (!hasWorkflowActionPermission) {
      return false;
    }
    if (!isHandoffToSignatureHop(node)) {
      return false;
    }
    const status = currentWorkflowUiStatus(node);
    if (status !== 'edit') {
      return false;
    }
    if (workflowTemplate?.steps?.length && workflowTemplateHasReviewPhase(workflowTemplate.steps)) {
      return false;
    }
    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const isCurrentStepPublishDefinition = (): boolean =>
    currentDefinitionType().localeCompare('Publish', undefined, { sensitivity: 'accent' }) === 0;

  const canApprove = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (status !== 'review')
      return false;
    
    if (isCurrentStepPublishDefinition())
      return false;

    if (!workflowActionAllowed('Approve'))
      return false;
    
    if (!hasWorkflowActionPermission)
      return false;
    
    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const canReject = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;
    
    if (!currentStepRuntime?.allowedActions?.includes('Reject'))
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (status !== 'review' && status !== 'signature')
      return false;
    
    if (!workflowActionAllowed('Reject'))
      return false;
    
    if (!hasWorkflowActionPermission)
      return false;

    if (status === 'signature') {
      const inProgressSig = pickInProgressSignatureRequest(activeSignatureRequest, latestSignatureRequest);
      if (!inProgressSig) {
        return false;
      }
      const mySigner = findMySignerInWorkflowRequest(inProgressSig, user?.id, user?.email);
      if (mySigner && signerStatusMeansDone(mySigner.status)) {
        return false;
      }
    }
    
    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const canWorkflowPublish = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (!workflowActionAllowed('Publish'))
      return false;
    
    if (!hasWorkflowActionPermission)
      return false;
    
    const responsible = user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
    if (!responsible)
      return false;
    
    if (status === 'edit') {
      const templateSteps = workflowTemplate?.steps ?? [];
      if (workflowTemplateHasReviewPhase(templateSteps) || workflowTemplateHasSignaturePhase(templateSteps))
        return false;

      return true;
    }
    
    if (isCurrentStepPublishDefinition())
      return true;

    return false;
  };

  const canArchiveWithWorkflow = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;

    const status = currentWorkflowUiStatus(node);
    if (status !== 'published')
      return false;

    if (postPublishBlockedBySignature)
      return false;
    
    if (!workflowActionAllowed('Archive'))
      return false;
    
    if (!hasWorkflowActionPermission)
      return false;

    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const canSignWorkflow = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (status !== 'signature')
      return false;
    
    const hasSign = workflowActionAllowed('Sign');
    if (!hasSign)
      return false;
    
    if (!hasWorkflowActionPermission)
      return false;

    if (!(user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep))) {
      return false;
    }

    const inProgressSig = pickInProgressSignatureRequest(activeSignatureRequest, latestSignatureRequest);
    return findMyPendingSignerForWorkflow(inProgressSig, user?.id, user?.email) !== null;
  };

  const getEditablePlacementRequest = (): DocumentSignatureDto | null => {
    if (canEditSignaturePlacements(activeSignatureRequest))
    {
      return activeSignatureRequest;
    }

    if (canEditSignaturePlacements(latestSignatureRequest))
    {
      return latestSignatureRequest;
    }

    return null;
  };

  /** Abre o modal de posicionamento e adia a transição. Retorna true se o fluxo foi capturado. */
  const beginEdocPlacementBeforeAdvance = (action: string, comment?: string): boolean => {
    if (!selectedNode || selectedNode.nodeType !== 'edoc')
    {
      return false;
    }

    const nextStepType =
      workflowTemplate?.steps?.length && currentStepRuntime?.templateStep?.id
        ? getNextStepAfterAction(
            workflowTemplate.steps,
            currentStepRuntime.templateStep.id,
            action
          )?.type
        : currentStepRuntime?.nextTemplateStep?.type;

    if (!actionAdvancesToSignatureStep(action, nextStepType))
    {
      return false;
    }

    const request = getEditablePlacementRequest();
    if (!request)
    {
      return false;
    }

    setPendingSignaturePlacementModal({
      signatureRequestId: request.id,
      action,
      comment,
    });
    return true;
  };

  const canSignStandaloneSignature = (node: DocumentNode): boolean => {
    if (node.nodeType === 'folder')
    {
      return false;
    }

    if (!isWorkflowCapableNode(node))
    {
      return false;
    }

    if (!applyStandaloneSignatureRules)
    {
      return false;
    }

    if (documentWorkflow && workflowUiLoading)
    {
      return false;
    }

    if (!activeSignatureRequest)
    {
      return false;
    }

    if (!isInProgressSignatureRequestStatus(activeSignatureRequest.status))
    {
      return false;
    }

    if (!permissions.canView)
    {
      return false;
    }

    if (!user?.id)
    {
      return false;
    }

    const me = findMyPendingSignerForWorkflow(activeSignatureRequest, user.id, user.email);
    if (!me)
    {
      return false;
    }

    return true;
  };

  const canCompleteEditWorkflow = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (status !== 'edit')
      return false;
    
    if (!workflowActionAllowed('CompleteEdit'))
      return false;
    
    const nextType = (currentStepRuntime.nextTemplateStep?.type || '').trim();
    if (nextType && nextType.localeCompare('Edit', undefined, { sensitivity: 'accent' }) !== 0)
      return false;
    
    if (!hasWorkflowActionPermission)
      return false;

    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const canSaveWorkflow = (node: DocumentNode): boolean => {
    if (!currentStepRuntime?.hasOpenStep)
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (status !== 'edit')
      return false;
    if (!workflowActionAllowed('Save'))
      return false;
    if (!hasWorkflowActionPermission)
      return false;
    
    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const canForkToNewVersion = (node: DocumentNode): boolean => {
    if (!currentStepRuntime)
      return false;
    
    const status = currentWorkflowUiStatus(node);
    if (status !== 'published' && status !== 'archived')
      return false;

    if (postPublishBlockedBySignature)
      return false;
    
    if (!workflowActionAllowed('Edit'))
      return false;
    
    if (!hasWorkflowActionPermission)
      return false;
    
    return user?.isAdmin === true || isUserResponsibleForTemplateStep(currentStepRuntime.templateStep);
  };

  const canEditDocument = (): boolean => {
    if (!documentWorkflow)
      return true;

    if (!currentStepRuntime)
      return true;

    if (!currentStepRuntime.hasOpenStep)
      return false;

    return currentStepRuntime.allowedActions.includes('Save');
  };

  const hasBlockingSignatureRequest = (node: DocumentNode | null | undefined): boolean => {
    if (!node?.id || node.id === 'root')
    {
      return false;
    }

    const candidates = [activeSignatureRequest, latestSignatureRequest].filter(
      (sig): sig is DocumentSignatureDto =>
        Boolean(sig && documentIdsMatch(sig.documentId, node.id))
    );

    return candidates.some(
      (sig) =>
        isInProgressSignatureRequestStatus(sig.status) ||
        isCompletedSignatureRequestStatus(sig.status)
    );
  };

  const canDirectPublish = (): boolean =>
    !!currentStepRuntime?.hasOpenStep && currentStepRuntime.allowedActions.includes('Publish');

  const canShowAnyWorkflowTransition = (node: DocumentNode): boolean =>
    canSignStandaloneSignature(node) ||
    (canShowWorkflowActions(node) &&
      (canSendToReview(node) ||
        canApprove(node) ||
        canReject(node) ||
        canWorkflowPublish(node) ||
        canArchiveWithWorkflow(node) ||
        canSendForSignature(node) ||
        canSignWorkflow(node) ||
        canSaveWorkflow(node) ||
        canCompleteEditWorkflow(node) ||
        canForkToNewVersion(node)));

  useEffect(() => {
    if (selectedNode?.id) {
      refreshStatus();
    }
  }, [selectedNode?.id, selectedNode?.status, refreshStatus]);

  // Carregar categoria e tags do documento quando selecionado
  useEffect(() => {
    if (selectedNode && selectedNode.nodeType !== 'folder') {
      setDocumentCategory(selectedNode.metadata?.category || '');
      // Usar as categorias e tags diretamente do documento
      setDocumentCategories(selectedNode.categories?.map(cat => cat.name) || []);
      setDocumentTags(selectedNode.tags?.map(tag => tag.name) || []);
      // Carregar descrição do documento
      setDocumentDescription(selectedNode.description || '');
    }
  }, [selectedNode]);

  useEffect(() => {
    setShowRenameModal(false);
    setShowShareModal(false);
  }, [selectedNode?.id]);

  useEffect(() => {
    if (selectedNode?.id && selectedNode.nodeType !== 'folder') {
      setLoadingRelated(true);
      documentRelationshipService
        .getByDocumentId(selectedNode.id)
        .then(setRelatedDocuments)
        .catch(() => setRelatedDocuments([]))
        .finally(() => setLoadingRelated(false));
    } else {
      setRelatedDocuments([]);
    }
  }, [selectedNode?.id]);

  const handleRemoveRelationship = async (relationshipId: string) => {
    if (!window.confirm('Deseja remover este relacionamento?')) return;
    try {
      await documentRelationshipService.remove(relationshipId);
      setRelatedDocuments(prev => prev.filter(r => r.id !== relationshipId));
      addToast('Relacionamento removido com sucesso.', 'success');
    } catch {
      addToast('Erro ao remover relacionamento.', 'error');
    }
  };

  const getRelationshipBadgeColor = (isSystem: boolean): string =>
    isSystem ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';

  /**
   * Convenção de produto para tipos com leitura direcional na UI:
   * na criação, source = documento aberto ao vincular, target = documento escolhido na busca.
   * "Dependente" = o source declara depender do target. "Referência" = o source referencia o target.
   */
  const getRelationshipDirectionHint = (
    relationshipType: string,
    isSource: boolean,
  ): string | null => {
    const t = relationshipType.trim();
    if (t === 'Dependente') {
      return isSource
        ? 'Indicação: este documento declara depender do documento relacionado.'
        : 'Indicação: o documento relacionado declara depender deste documento.';
    }
    if (t === 'Referência') {
      return isSource
        ? 'Indicação: este documento referencia o documento relacionado.'
        : 'Indicação: o documento relacionado referencia este documento.';
    }
    return null;
  };

  // Cleanup do timeout quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (descriptionTimeout) {
        clearTimeout(descriptionTimeout);
      }
    };
  }, [descriptionTimeout]);

  const handleDescriptionChange = async (description: string) => {
    if (!selectedNode) return;
    
    setDocumentDescription(description);
    
    // Cancelar timeout anterior
    if (descriptionTimeout) {
      clearTimeout(descriptionTimeout);
    }
    
    // Criar novo timeout para salvar após 1 segundo de inatividade
    const timeout = setTimeout(async () => {
      setIsSavingDescription(true);
      
      try {
        await documentService.updateDocumentDescription(selectedNode.id, description);
        addToast('Descrição salva com sucesso', 'success');
        
        // Atualizar o documento pai
        onDocumentUpdated(selectedNode.id);
        
        // Atualizar o índice do Elasticsearch se o documento estiver indexado
        // Não falhar se der erro na atualização do índice
        updateIndexIfNeeded(selectedNode.id);
      } catch (error) {
        addToast('Erro ao salvar descrição', 'error');
      } finally {
        setIsSavingDescription(false);
      }
    }, 1000);
    
    setDescriptionTimeout(timeout);
  };

  const canBeAddedToChatbot = (node: DocumentNode): boolean => {
    // Não pode ser pasta
    if (node.nodeType === 'folder') return false;

    // Verificar tipo de arquivo
    const contentType = node.metadata?.type?.toLowerCase() || '';
    const excludedTypes = ['video', 'image', 'compressed', 'audio'];
    
    // Verificar se é um tipo excluído
    if (excludedTypes.some(type => contentType.includes(type))) return false;

    // Verificar se o documento está publicado
    if (statusForNode(node) !== 'published') return false;

    return true;
  };

  // Nova função para verificar se pode ser indexado no Elasticsearch
  const canBeIndexedInElasticsearch = (node: DocumentNode): boolean => {
    // Não pode ser pasta
    if (node.nodeType === 'folder') return false;

    // Deve estar publicado
    if (statusForNode(node) !== 'published') return false;

    return true;
  };

  // Função para verificar se deve mostrar a seção de ações
  const shouldShowActionsSection = (node: DocumentNode): boolean => {
    return canBeIndexedInElasticsearch(node) || canBeAddedToChatbot(node);
  };

  // Função para obter status do chatbot (usando o hook)
  const indexedWorkspaceIds = documentStatus?.indexedWorkspaceIds ?? [];
  const isInChatbot = indexedWorkspaceIds.length > 0;

  const indexedWorkspaceIdsKey = indexedWorkspaceIds.join(',');

  useEffect(() => {
    setLinkedChatbots([]);
  }, [contextTenantId]);

  useEffect(() => {
    const documentId = selectedNode?.id;

    if (!documentId || indexedWorkspaceIdsKey.length === 0)
    {
      setLinkedChatbots([]);
      return;
    }

    let cancelled = false;
    const indexedIds = indexedWorkspaceIdsKey.split(',');

    const loadLinkedChatbots = async () => {
      setLinkedChatbotsLoading(true);
      try {
        const workspaces = await workspaceService.getWorkspacesForDocument(documentId, {
          skipFeatureUnavailableToast: true,
        });

        if (!cancelled)
        {
          const indexedSet = new Set(indexedIds);
          setLinkedChatbots(workspaces.filter((workspace) => indexedSet.has(workspace.id)));
        }
      } catch {
        if (!cancelled)
        {
          setLinkedChatbots([]);
        }
      } finally {
        if (!cancelled)
        {
          setLinkedChatbotsLoading(false);
        }
      }
    };

    void loadLinkedChatbots();

    return () => {
      cancelled = true;
    };
  }, [selectedNode?.id, indexedWorkspaceIdsKey, contextTenantId]);

  const addChatbotLoading = useCallback((documentId: string) => {
    setChatbotLoadingDocuments((prev) => {
      const next = new Set(prev);
      next.add(documentId);
      return next;
    });
  }, []);

  const removeChatbotLoading = useCallback((documentId: string) => {
    setChatbotLoadingDocuments((prev) => {
      if (!prev.has(documentId))
      {
        return prev;
      }

      const next = new Set(prev);
      next.delete(documentId);
      return next;
    });
  }, []);

  const setPendingChatbotAction = useCallback((
    documentId: string,
    action: 'add' | 'remove',
    snapshotIndexedWorkspaceIds: string[],
  ) => {
    pendingChatbotActionsRef.current.set(documentId, {
      action,
      indexedWorkspaceIds: [...snapshotIndexedWorkspaceIds],
    });
  }, []);

  const clearPendingChatbotAction = useCallback((documentId: string) => {
    pendingChatbotActionsRef.current.delete(documentId);
  }, []);

  const reconcileChatbotPendingState = useCallback((
    documentId: string,
    currentIndexedWorkspaceIds: string[],
  ) => {
    const pendingState = pendingChatbotActionsRef.current.get(documentId);
    if (!pendingState)
    {
      removeChatbotLoading(documentId);
      return;
    }

    const snapshotKey = pendingState.indexedWorkspaceIds.slice().sort().join(',');
    const currentKey = currentIndexedWorkspaceIds.slice().sort().join(',');
    const workspaceIdsChanged = snapshotKey !== currentKey;

    if (workspaceIdsChanged)
    {
      clearPendingChatbotAction(documentId);
      removeChatbotLoading(documentId);
    }
  }, [clearPendingChatbotAction, removeChatbotLoading]);

  useEffect(() => {
    const documentId = selectedNode?.id;
    selectedNodeIdRef.current = documentId;
    setShowAddToChatbotModal(false);
    setShowRemoveFromChatbotModal(false);
    setShowManageChatbotsModal(false);

    if (!documentId)
    {
      return;
    }

    if (!pendingChatbotActionsRef.current.has(documentId))
    {
      removeChatbotLoading(documentId);
    }
  }, [selectedNode?.id, removeChatbotLoading]);

  useEffect(() => {
    if (!selectedNode?.id || documentStatus == null)
    {
      return;
    }

    reconcileChatbotPendingState(
      selectedNode.id,
      documentStatus.indexedWorkspaceIds ?? [],
    );
  }, [selectedNode?.id, documentStatus?.indexedWorkspaceIds, reconcileChatbotPendingState]);

  useEffect(() => {
    const documentId = selectedNode?.id;
    if (!documentId || !chatbotLoadingDocuments.has(documentId))
    {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refreshStatus();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [selectedNode?.id, chatbotLoadingDocuments, refreshStatus]);

  useEffect(() => {
    const handleChatbotIndexingUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{
        action: 'add' | 'remove';
        success: boolean;
        documentId?: string;
      }>;
      const detail = customEvent.detail;

      if (!detail?.documentId)
      {
        return;
      }

      const documentId = detail.documentId;
      const pendingState = pendingChatbotActionsRef.current.get(documentId);
      const isCurrentDocument = selectedNodeIdRef.current === documentId;

      if (!isCurrentDocument)
      {
        return;
      }

      const syncStatus = async () => {
        await refreshStatus();
      };

      if (pendingState && pendingState.action === detail.action)
      {
        void syncStatus().finally(() => {
          clearPendingChatbotAction(documentId);
          removeChatbotLoading(documentId);
        });
        return;
      }

      void syncStatus();
    };

    window.addEventListener('chatbot-indexing-update', handleChatbotIndexingUpdate as EventListener);

    return () => {
      window.removeEventListener('chatbot-indexing-update', handleChatbotIndexingUpdate as EventListener);
    };
  }, [refreshStatus, clearPendingChatbotAction, removeChatbotLoading]);

  const handleChatbotToggle = async () => {
    if (!selectedNode)
    {
      return;
    }

    const documentId = selectedNode.id;

    if (isInChatbot)
    {
      const currentIndexedWorkspaceIds = documentStatus?.indexedWorkspaceIds ?? [];

      if (currentIndexedWorkspaceIds.length === 1)
      {
        await handleRemoveFromChatbotConfirm([currentIndexedWorkspaceIds[0]], documentId);
        return;
      }

      if (currentIndexedWorkspaceIds.length > 1)
      {
        setShowRemoveFromChatbotModal(true);
        return;
      }

      await handleRemoveFromChatbotConfirm([LEGACY_REMOVE_WORKSPACE_ID], documentId);
    }
    else
    {
      addChatbotLoading(documentId);
      try
      {
        const accessibleWorkspaces = (await workspaceService.getWorkspacesForDocument(documentId))
          .filter((w) => w.isActive)
          .filter((w) => w.isAccessible)
          .filter((w) => !(documentStatus?.indexedWorkspaceIds ?? []).includes(w.id));

        if (accessibleWorkspaces.length === 1)
        {
          await handleAddToChatbotConfirm([accessibleWorkspaces[0].id], documentId);
          return;
        }

        removeChatbotLoading(documentId);
        setShowAddToChatbotModal(true);
      }
      catch
      {
        removeChatbotLoading(documentId);
        setShowAddToChatbotModal(true);
      }
    }
  };

  const handleAddToChatbotConfirm = async (workspaceIds: string[], documentIdOverride?: string) => {
    const documentId = documentIdOverride ?? selectedNode?.id;
    if (!documentId || workspaceIds.length === 0)
    {
      return;
    }

    addChatbotLoading(documentId);
    try
    {
      let anySuccess = false;
      for (const workspaceId of workspaceIds)
      {
        const success = await chatbotService.addDocumentToChatbot(documentId, workspaceId);
        if (success)
        {
          anySuccess = true;
        }
      }

      if (anySuccess)
      {
        setShowAddToChatbotModal(false);
        setPendingChatbotAction(documentId, 'add', documentStatus?.indexedWorkspaceIds ?? []);
        addToast('Documento adicionado à fila de processamento do chatbot. Você será notificado quando o processamento for concluído.', 'info');
        return;
      }
    }
    catch (error: any)
    {
      addToast(error.message || 'Erro ao adicionar documento ao chatbot. Tente novamente.', 'error');
    }
    removeChatbotLoading(documentId);
  };

  const handleRemoveFromChatbotConfirm = async (workspaceIds: string[], documentIdOverride?: string) => {
    const documentId = documentIdOverride ?? selectedNode?.id;
    if (!documentId || workspaceIds.length === 0)
    {
      return;
    }

    addChatbotLoading(documentId);
    try
    {
      let anySuccess = false;
      for (const workspaceId of workspaceIds)
      {
        const success = await chatbotService.removeDocumentFromChatbot(documentId, workspaceId);
        if (success)
        {
          anySuccess = true;
        }
      }

      if (anySuccess)
      {
        setShowRemoveFromChatbotModal(false);
        setPendingChatbotAction(documentId, 'remove', documentStatus?.indexedWorkspaceIds ?? []);
        addToast('Documento adicionado à fila de remoção do chatbot. Você será notificado quando o processamento for concluído.', 'info');
        return;
      }
    }
    catch (error: any)
    {
      addToast(error.message || 'Erro ao remover documento do chatbot. Tente novamente.', 'error');
    }
    removeChatbotLoading(documentId);
  };

  const handleManageChatbotsConfirm = async (workspacesToAdd: string[], workspacesToRemove: string[]) => {
    const documentId = selectedNode?.id;
    if (!documentId)
    {
      return;
    }

    addChatbotLoading(documentId);
    
    try
    {
      let addSuccessCount = 0;
      let removeSuccessCount = 0;
      
      if (workspacesToAdd.length > 0)
      {
        const result = await chatbotService.addDocumentToChatbotBatch(documentId, workspacesToAdd);
        addSuccessCount = result.successCount;
      }
      
      if (workspacesToRemove.length > 0)
      {
        const result = await chatbotService.removeDocumentFromChatbotBatch(documentId, workspacesToRemove);
        removeSuccessCount = result.successCount;
      }

      if (addSuccessCount > 0 || removeSuccessCount > 0)
      {
        setShowManageChatbotsModal(false);
        
        const messages: string[] = [];
        if (addSuccessCount > 0)
        {
          messages.push(`${addSuccessCount} agente${addSuccessCount > 1 ? 's' : ''} na fila de indexação`);
        }
        if (removeSuccessCount > 0)
        {
          messages.push(`${removeSuccessCount} agente${removeSuccessCount > 1 ? 's' : ''} na fila de remoção`);
        }
        
        const action = addSuccessCount > 0 ? 'add' : 'remove';
        setPendingChatbotAction(documentId, action, documentStatus?.indexedWorkspaceIds ?? []);
        
        addToast(`Alterações aplicadas: ${messages.join(', ')}. Você será notificado quando o processamento for concluído.`, 'info');
        return;
      }
      
      addToast('Nenhuma alteração foi processada.', 'warning');
      removeChatbotLoading(documentId);
    }
    catch (error: any)
    {
      addToast(error.message || 'Erro ao processar alterações nos chatbots. Tente novamente.', 'error');
      removeChatbotLoading(documentId);
    }
  };

  // Função auxiliar para atualizar o índice se necessário
  const updateIndexIfNeeded = async (documentId: string) => {
    try {
      if (documentStatus?.indexedForSearch) {
        await indexingService.updateDocumentInIndex(documentId);
      }
    } catch (indexError) {
      // Não mostrar erro para o usuário, pois é uma operação em background
      // Não falhar a operação principal por causa da atualização do índice
    }
  };

  const handleCategoryChange = (category: string) => {
    setDocumentCategory(category);
    // Aqui você pode implementar a lógica para salvar a categoria no backend
    // Removido addToast pois DocumentCategoriesAndTags já exibe a mensagem
    // Atualizar o documento na árvore após a mudança
    if (selectedNode) {
      onDocumentUpdated(selectedNode.id);
      // Atualizar índice em background (não falhar se der erro)
      updateIndexIfNeeded(selectedNode.id);
    }
  };

  const handleCategoriesChange = (categories: string[]) => {
    setDocumentCategories(categories);
    // Aqui você pode implementar a lógica para salvar as categorias no backend
    // Removido addToast pois DocumentCategoriesAndTags já exibe a mensagem
    // Atualizar o documento na árvore após a mudança
    if (selectedNode) {
      onDocumentUpdated(selectedNode.id);
      // Atualizar índice em background (não falhar se der erro)
      updateIndexIfNeeded(selectedNode.id);
    }
  };

  const handleTagsChange = (tags: string[]) => {
    setDocumentTags(tags);
    // Aqui você pode implementar a lógica para salvar as tags no backend
    // Removido addToast pois DocumentCategoriesAndTags já exibe a mensagem
    // Atualizar o documento na árvore após a mudança
    if (selectedNode) {
      onDocumentUpdated(selectedNode.id);
      // Atualizar índice em background (não falhar se der erro)
      updateIndexIfNeeded(selectedNode.id);
    }
  };

  // Função para obter status do Elasticsearch (usando o hook)
  const isIndexedInElasticsearch = documentStatus?.indexedForSearch || false;

  // Função para indexação semântica
  const handleSemanticIndexing = async () => {
    if (!selectedNode) return;
    
    setIsLoadingIndexing(true);
    try {
      let response;
      
      if (isIndexedInElasticsearch) {
        response = await indexingService.removeDocumentFromIndex(selectedNode.id);
      } else {
        response = await indexingService.indexDocumentSemantic(selectedNode.id);
      }

      try {
        await indexingService.commitIndexChanges();
      } catch (commitError) {
        // FTS persiste na transação; Lucene pode falhar o commit sem desfazer a operação.
      }

      try {
        await refreshStatus();
      } catch (statusError) {
      }
      
      addToast(response.message, 'success');
    } catch (error) {
      addToast('Erro ao alterar status de indexação. Tente novamente.', 'error');
    } finally {
      setIsLoadingIndexing(false);
    }
  };

  // Função para processar conteúdo HTML para permitir navegação adequada
  const processHtmlContent = (htmlContent: string): string => {
    // Criar um DOM parser temporário
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Processar todos os links para evitar interferência na aplicação principal
    const links = doc.querySelectorAll('a');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href && !href.startsWith('#')) {
        // Remover target="_blank" pois será controlado pelo JavaScript
        link.removeAttribute('target');
        // Remover rel="noopener noreferrer" pois será controlado pelo JavaScript
        link.removeAttribute('rel');
        // Adicionar classe para identificação
        link.classList.add('external-link');
      }
    });
    
    // Processar todos os scripts para garantir que funcionem
    const scripts = doc.querySelectorAll('script');
    scripts.forEach(script => {
      // Remover atributos que podem causar problemas
      script.removeAttribute('integrity');
      script.removeAttribute('crossorigin');
    });
    
    // Remover qualquer script que possa tentar redirecionar a página
    const redirectScripts = doc.querySelectorAll('script');
    redirectScripts.forEach(script => {
      const scriptContent = script.textContent || '';
      if (scriptContent.includes('window.location') || 
          scriptContent.includes('location.href') || 
          scriptContent.includes('location.replace')) {
        script.remove();
      }
    });
    
    // Retornar o HTML processado
    return doc.documentElement.outerHTML;
  };

  if (!selectedNode) {
    return (
      <div className="text-center text-gray-500 py-8">
        Selecione um documento ou pasta para visualizar
      </div>
    );
  }

  const getFileExtension = (node: DocumentNode): string => {
    const fileName = node.metadata?.fileName || node.title || '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const getFileType = (node: DocumentNode): string => {
    const ext = getFileExtension(node);
    const contentType = node.metadata?.contentType || '';
    
    if (ext === 'pdf') return 'pdf';
    if (ext === 'html' || ext === 'htm') return 'html';
    if (ext === 'txt') return 'text';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext)) return 'image';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(ext)) return 'audio';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['xls', 'xlsx'].includes(ext)) return 'excel';
    if (['odt'].includes(ext)) return 'odt';
    if (['odg'].includes(ext)) return 'odg';
    if (['odp'].includes(ext)) return 'odp';
    if (['ods'].includes(ext)) return 'ods';
    if (['edoc'].includes(ext)) return 'edoc';
    if (['zip', '7z', 'rar', 'tar', 'gz', 'bz2'].includes(ext)) return 'compressed';
    return 'other';
  };

  const handleViewDocument = async () => {
    if (!selectedNode) return;

    setIsViewing(true);
    
    const fileType = getFileType(selectedNode);
    const isPdfView =
      fileType === 'pdf' || selectedNode.nodeType === 'pdf';

    try {
      // Verificar ID válido para todos os tipos de documentos
      if (!selectedNode.id || selectedNode.id === 'root') {
        addToast('ID do documento inválido', 'error');
        return;
      }

      // Verificar tamanho do arquivo antes de tentar visualizar
      const fileSize = selectedNode.metadata?.size;
      if (fileSize) {
        const sizeInMB = parseInt(fileSize) / (1024 * 1024);
        
        // Limites ajustados conforme solicitado
        if (isPdfView && sizeInMB > 20) {
          addToast(
            `Este arquivo PDF é muito grande (${sizeInMB.toFixed(1)} MB). Use a opção Baixar para arquivos maiores que 20MB.`, 
            'warning'
          );
          return;
        } else if (['html', 'edoc'].includes(fileType) && sizeInMB > 30) {
          addToast(
            `Este arquivo ${fileType.toUpperCase()} é muito grande (${sizeInMB.toFixed(1)} MB). Use a opção Baixar para arquivos maiores que 30MB.`, 
            'warning'
          );
          return;
        } else if (fileType === 'text' && sizeInMB > 20) {
          addToast(
            `Este arquivo de texto é muito grande (${sizeInMB.toFixed(1)} MB). Use a opção Baixar para arquivos maiores que 20MB.`, 
            'warning'
          );
          return;
        } else if (fileType === 'image' && sizeInMB > 30) {
          addToast(
            `Esta imagem é muito grande (${sizeInMB.toFixed(1)} MB). Use a opção Baixar para imagens maiores que 30MB.`, 
            'warning'
          );
          return;
        } else if (['word', 'excel', 'odt', 'odg', 'odp', 'ods'].includes(fileType) && sizeInMB > 35) {
          addToast(
            `Este arquivo ${fileType.toUpperCase()} é muito grande (${sizeInMB.toFixed(1)} MB). Use a opção Baixar para arquivos maiores que 35MB.`, 
            'warning'
          );
          return;
        }
      }

      // Mostrar indicador de carregamento
      setIsLoading(true);
      
      // Para PDF e eDoc, preferir PDF final → parcial → original (mesma regra da área de assinatura).
      // eDoc assinado é gerado como PDF; só cai no DocumentViewer quando não há PDF assinado disponível.
      const isEdocView =
        fileType === 'edoc' || selectedNode.nodeType === 'edoc';

      if (isPdfView || isEdocView) {
        try {
          const sig = latestSignatureRequest;
          const { blob, source } = await resolveDocumentBlobForSignature(selectedNode.id, sig);
          const hasCollectedSignatures = Boolean(
            sig &&
              documentIdsMatch(sig.documentId, selectedNode.id) &&
              signatureHasCollectedSignatures(sig)
          );
          const waitingForSignedPreview =
            source === 'original' &&
            hasCollectedSignatures &&
            sig?.partialSignedDocumentFailed !== true;

          // PDF e eDoc: enquanto a prévia parcial/final não existir, usar DocumentViewer
          // (loading + retry). eDoc sem assinaturas também usa DocumentViewer para o HTML.
          if (waitingForSignedPreview || (isEdocView && source === 'original'))
          {
            setShowDocumentViewer(true);
          }
          else
          {
            if (
              source !== 'final' &&
              sig &&
              documentIdsMatch(sig.documentId, selectedNode.id) &&
              sig.finalSignedDocumentProcessing === true
            ) {
              addToast(
                source === 'partial'
                  ? 'Exibindo a prévia com assinaturas. O PDF consolidado ainda está sendo gerado.'
                  : 'Exibindo a versão atual do arquivo. O PDF consolidado com assinaturas ainda está sendo gerado.',
                'info'
              );
            } else if (
              source !== 'final' &&
              sig &&
              documentIdsMatch(sig.documentId, selectedNode.id) &&
              sig.finalSignedDocumentFailed === true
            ) {
              addToast(
                source === 'partial'
                  ? 'Exibindo a prévia com assinaturas. A geração do PDF consolidado falhou.'
                  : 'A geração do PDF consolidado falhou. Exibindo a versão atual do arquivo.',
                'warning'
              );
            } else if (
              source === 'original' &&
              sig &&
              documentIdsMatch(sig.documentId, selectedNode.id) &&
              sig.partialSignedDocumentFailed === true
            ) {
              addToast(
                'A geração da prévia com assinaturas falhou. Exibindo o documento original. As assinaturas já estão registradas.',
                'warning'
              );
            } else if (
              source === 'partial' &&
              sig &&
              documentIdsMatch(sig.documentId, selectedNode.id) &&
              sig.partialSignedDocumentFailed === true
            ) {
              addToast(
                'Exibindo a última prévia disponível. A atualização mais recente da prévia falhou.',
                'warning'
              );
            }

            const url = window.URL.createObjectURL(blob);

            setViewContent(
              `<iframe src="${url}" type="application/pdf" width="100%" height="600px" frameborder="0"></iframe>`
            );
            setShowViewModal(true);

            const originalCleanup = () => {
              window.URL.revokeObjectURL(url);
              setShowViewModal(false);
            };

            setCleanupFunction(() => originalCleanup);
          }
        } catch (error) {
          throw error;
        }
      }

      // Para HTML, mostrar conteúdo em modal
      else if (fileType === 'html') {
        try {
          const blob = await documentDownloadService.downloadDocument(selectedNode.id);
          const content = await blob.text();
          
          // Verificar tamanho do conteúdo HTML (limite de 30MB)
          if (content.length > 30 * 1024 * 1024) {
            addToast(
              'Este arquivo HTML é muito grande para visualização. Use a opção Baixar.', 
              'warning'
            );
            return;
          }
          
          // Processar o conteúdo HTML para permitir navegação adequada
          const processedContent = processHtmlContent(content);
          
          // Usar div nativo para renderizar HTML
          setViewContent(`
            <div id="html-viewer" style="width: 100%; height: 600px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 0.375rem; padding: 1rem;">
              ${processedContent}
            </div>
            <script>
              (function() {
                const viewer = document.getElementById('html-viewer');
                if (viewer) {
                  // Interceptar cliques em links para evitar navegação na aplicação principal
                  viewer.addEventListener('click', function(e) {
                    if (e.target.tagName === 'A') {
                      const href = e.target.getAttribute('href');
                      if (href && !href.startsWith('#'') && !href.startsWith('javascript:')) {
                        e.preventDefault();
                        e.stopPropagation();
                         
                        // Abrir link em nova aba
                        const newWindow = window.open(href, '_blank', 'noopener,noreferrer');
                        if (newWindow) {
                          newWindow.focus();
                        } else {
                          // Fallback: mostrar mensagem se popup for bloqueado
                          addToast('Link: ' + href + ' - Pop-up bloqueado. Copie o link e cole em uma nova aba.', 'warning');
                        }
                      }
                    }
                  });
                  
                  // Interceptar formulários para evitar submissão na aplicação principal
                  const forms = viewer.querySelectorAll('form');
                  forms.forEach(form => {
                    form.addEventListener('submit', function(e) {
                      e.preventDefault();
                      e.stopPropagation();
                      addToast('Formulários não são suportados na visualização de documentos.', 'warning');
                    });
                  });
                  
                  // Interceptar scripts que tentam redirecionar
                  const scripts = viewer.querySelectorAll('script');
                  scripts.forEach(script => {
                    try {
                      const scriptContent = script.textContent || '';
                      if (scriptContent.includes('window.location') || 
                          scriptContent.includes('location.href') || 
                          scriptContent.includes('location.replace')) {
                        script.remove();
                      }
                    } catch (error) {
                      // Ignorar erros de segurança
                    }
                  });
                }
              })();
            </script>
          `);
          setShowViewModal(true);
        } catch (error) {
          throw error;
        }
      }

      // Para texto, mostrar conteúdo formatado
      else if (fileType === 'text') {
        try {
          const blob = await documentDownloadService.downloadDocument(selectedNode.id);
          const content = await blob.text();
          
          // Verificar tamanho do conteúdo de texto (limite de 5MB)
          if (content.length > 5 * 1024 * 1024) {
            addToast(
              'Este arquivo de texto é muito grande para visualização. Use a opção Baixar.', 
              'warning'
            );
            return;
          }
          
          setViewContent(`<pre style="white-space: pre-wrap; word-wrap: break-word; max-height: 600px; overflow-y: auto;">${content}</pre>`);
          setShowViewModal(true);
        } catch (error) {
          throw error;
        }
      }

      // Para imagens, mostrar em modal
      else if (fileType === 'image') {
        try {
          const blob = await documentDownloadService.downloadDocument(selectedNode.id);
          const url = window.URL.createObjectURL(blob);
          
          setViewContent(`<img src="${url}" alt="${selectedNode.title}" style="max-width: 100%; max-height: 80vh; display: block; margin: 0 auto;">`);
          setShowViewModal(true);
          
          // Configurar limpeza
          setCleanupFunction(() => () => {
            window.URL.revokeObjectURL(url);
            setShowViewModal(false);
          });
        } catch (error) {
          throw error;
        }
      }

      // Para vídeo e áudio, informar que é necessário fazer download
      else if (['video', 'audio'].includes(fileType)) {
        addToast(
          `Para arquivos de ${fileType === 'video' ? 'vídeo' : 'áudio'}, é necessário fazer o download. Use a opção Baixar.`, 
          'info'
        );
        return;
      }

      // Para formatos OpenDocument (ODT, ODP, ODS)
      else if (['odt', 'odp', 'ods', 'odg'].includes(fileType)) {
        setShowOpenDocumentViewer(true);
      }
      
      // Para outros tipos de arquivo suportados pelo DocumentViewer
      else if (['word', 'excel', 'text', 'html', 'image'].includes(fileType)) {
        setShowDocumentViewer(true);
      }

      // Para outros tipos de arquivos não suportados
      else {
        addToast(`Tipo de arquivo não suportado para visualização: ${fileType}. Use a opção Baixar.`, 'info');
      }

    } catch (error) {
      let errorMessage = 'Erro ao visualizar documento';
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          errorMessage = 'Não autorizado. Faça login novamente.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Documento não encontrado.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Acesso negado a este documento.';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Erro de conexão. Verifique sua internet ou tente novamente.';
        } else {
          errorMessage = error.message;
        }
      }
      
      addToast(errorMessage, 'error');
    } finally {
      // Desativar indicadores de carregamento
      setIsLoading(false);
      setIsViewing(false);
    }
  };

  const downloadNodeToDisk = async (node: DocumentNode) => {
    if (!node.id || node.id === 'root') {
      throw new Error('ID do documento inválido');
    }
    await documentDownloadService.downloadAndSaveDocument(
      node.id,
      node.metadata?.fileName || node.title
    );
  };

  const handleDownload = async () => {
    if (!selectedNode) return;

    setIsDownloading(true);
    try {
      const sig = latestSignatureRequest;
      const fileName = selectedNode.metadata?.fileName || selectedNode.title || 'documento';
      const source = await downloadDocumentForSignature(selectedNode.id, fileName, sig);

      if (source === 'final') {
        addToast('PDF com assinaturas baixado com sucesso!', 'success');
        return;
      }

      if (
        sig &&
        documentIdsMatch(sig.documentId, selectedNode.id) &&
        sig.finalSignedDocumentProcessing === true
      ) {
        addToast(
          source === 'partial'
            ? 'Baixamos a prévia com assinaturas. O PDF consolidado ainda está sendo gerado.'
            : 'Baixamos a versão atual do arquivo. O PDF consolidado com assinaturas ainda está sendo gerado.',
          'info'
        );
      } else if (
        sig &&
        documentIdsMatch(sig.documentId, selectedNode.id) &&
        sig.finalSignedDocumentFailed === true
      ) {
        addToast(
          source === 'partial'
            ? 'Baixamos a prévia com assinaturas. A geração do PDF consolidado falhou.'
            : 'A geração do PDF consolidado falhou. Baixamos a versão atual do arquivo.',
          'warning'
        );
      } else if (
        source === 'original' &&
        sig &&
        documentIdsMatch(sig.documentId, selectedNode.id) &&
        sig.partialSignedDocumentFailed === true
      ) {
        addToast(
          'A geração da prévia com assinaturas falhou. Baixamos o documento original. As assinaturas já estão registradas.',
          'warning'
        );
      } else if (
        source === 'original' &&
        sig &&
        documentIdsMatch(sig.documentId, selectedNode.id) &&
        signatureHasCollectedSignatures(sig)
      ) {
        addToast(
          'Prévia com assinaturas ainda não disponível. Baixamos o documento original. As assinaturas já estão registradas.',
          'warning'
        );
      } else if (
        source === 'partial' &&
        sig &&
        documentIdsMatch(sig.documentId, selectedNode.id) &&
        sig.partialSignedDocumentFailed === true
      ) {
        addToast(
          'Baixamos a última prévia disponível. A atualização mais recente da prévia falhou.',
          'warning'
        );
      } else if (source === 'partial') {
        addToast('Prévia com assinaturas baixada com sucesso!', 'success');
      } else {
        addToast('Documento baixado com sucesso!', 'success');
      }
    } catch (error) {
      let errorMessage = 'Erro ao baixar documento';

      if (error instanceof Error) {
        if (error.message.includes('401')) {
          errorMessage = 'Não autorizado. Faça login novamente.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Documento não encontrado.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Acesso negado a este documento.';
        } else {
          errorMessage = error.message;
        }
      }

      addToast(errorMessage, 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadChild = async (child: DocumentNode) => {
    setDownloadingChildId(child.id);
    try {
      await downloadNodeToDisk(child);
      addToast('Documento baixado com sucesso!', 'success');
    } catch (error) {
      let errorMessage = 'Erro ao baixar documento';
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          errorMessage = 'Não autorizado. Faça login novamente.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Documento não encontrado.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Acesso negado a este documento.';
        } else {
          errorMessage = error.message;
        }
      }
      addToast(errorMessage, 'error');
    } finally {
      setDownloadingChildId(null);
    }
  };

  const handleDelete = () => {
    // Remove o modal interno e chama diretamente a função do pai
    if (selectedNode) {
      onDeleteDocument(selectedNode);
    }
  };

  const handlePermissions = () => {
    setShowPermissionsModal(true);
  };

  const handleWorkflowAction = (action: string) => {
    if (!selectedNode?.id || selectedNode.id === 'root') {
      addToast('Documento inválido para esta ação', 'error');
      return;
    }
    if (!currentStepRuntime) return;

    const st = statusForNode(selectedNode);
    if (
      action.localeCompare(WorkflowActionCodes.Edit, undefined, { sensitivity: 'accent' }) === 0 &&
      (st === 'published' || st === 'archived')
    ) {
      setWorkflowNewVersionModal(st === 'archived' ? 'archived' : 'published');
      return;
    }

    const requiresComment =
      currentStepRuntime.requiresComment || action.localeCompare('Reject', undefined, { sensitivity: 'accent' }) === 0;

    if (!requiresComment && beginEdocPlacementBeforeAdvance(action))
    {
      return;
    }

    setWorkflowTransitionModal({ action, requiresComment });
  };

  const handleSignaturePlacementConfirm = async (
    placements: Record<string, SignaturePlacementInputDto | null>
  ) => {
    if (!pendingSignaturePlacementModal || !selectedNode?.id)
    {
      return;
    }

    const { signatureRequestId, action, comment } = pendingSignaturePlacementModal;

    try {
      await documentSignatureService.updateSignersPlacements(signatureRequestId, placements);

      if (action === 'standalone')
      {
        await documentSignatureService.sendSignatureRequest(signatureRequestId);
        setPendingSignaturePlacementModal(null);
        addToast('Solicitação de assinatura enviada com sucesso!', 'success');
        await refreshDocumentSignatureState(selectedNode.id);
        onDocumentUpdated(selectedNode.id);
        return;
      }

      const result =
        action.localeCompare('Approve', undefined, { sensitivity: 'accent' }) === 0
          ? await documentWorkflowService.approve(selectedNode.id, comment)
          : await documentWorkflowService.executeTransition(selectedNode.id, action, comment);

      if (!result.success)
      {
        throw new Error(result.errorMessage || 'Não foi possível concluir a transição.');
      }

      setPendingSignaturePlacementModal(null);
      addToast('Posicionamento salvo e etapa avançada com sucesso!', 'success');
      await handleWorkflowTransitionSuccess(result);
    }
    catch (error)
    {
      const message = error instanceof Error ? error.message : 'Erro ao confirmar posicionamento de assinaturas';
      addToast(message, 'error');
      throw error;
    }
  };

  const handleWorkflowTransitionBeforeExecute = async (
    comment: string | undefined
  ): Promise<boolean> => {
    if (!workflowTransitionModal)
    {
      return true;
    }

    return !beginEdocPlacementBeforeAdvance(workflowTransitionModal.action, comment);
  };

  const handleArchivePublishedOutsideWorkflow = async () => {
    if (!selectedNode?.id || selectedNode.id === 'root') {
      addToast('Documento inválido para esta ação', 'error');
      return;
    }

    if (
      !window.confirm(
        'Arquivar este documento publicado? Ele não utiliza workflow: o estado passará a Arquivado e o agendamento de arquivo automático, se existir, será limpo.'
      )
    ) {
      return;
    }

    try {
      const dto = await documentService.archivePublishedOutsideWorkflow(selectedNode.id);
      setWorkflowStatusOverride(dto.status ?? 'archived');
      onNodeSelect({
        ...selectedNode,
        status: dto.status ?? selectedNode.status,
        currentVersion: dto.currentVersion ?? selectedNode.currentVersion,
        ...(dto.metadata ? { metadata: { ...(selectedNode.metadata ?? {}), ...dto.metadata } } : {}),
      });
      setLifecycleSchedule({
        publish: dto.scheduledPublishDate ?? null,
        archive: dto.scheduledArchiveDate ?? null,
      });
      setPublicationExpiresAt(dto.publicationExpiresAt ?? null);
      await refreshStatus();
      onDocumentUpdated(selectedNode.id);
      addToast('Documento arquivado.', 'success');
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { message?: string } } };
      const msg = ax.response?.data?.message || 'Não foi possível arquivar o documento.';
      addToast(msg, 'error');
    }
  };

  const refreshAfterDigitalSignature = async () => {
    if (!selectedNode?.id || selectedNode.id === 'root') {
      return;
    }
    await loadActiveWorkflow(selectedNode.id);
    await refreshDocumentSignatureState(selectedNode.id);
    await refreshStatus();
    onDocumentUpdated(selectedNode.id);
  };

  const handleWorkflowSignClick = () => {
    if (!selectedNode?.id || selectedNode.id === 'root') {
      addToast('Documento inválido para esta ação', 'error');
      return;
    }
    if (!user?.id || !user.email) {
      addToast('Usuário não autenticado.', 'error');
      return;
    }
    const sig = pickInProgressSignatureRequest(activeSignatureRequest, latestSignatureRequest);
    const me = findMyPendingSignerForWorkflow(sig, user.id, user.email);
    if (me) {
      const st = me.signatureType;
      if (
        st === SignatureTypeCode.Handwritten ||
        st === SignatureTypeCode.Electronic ||
        st === SignatureTypeCode.Certificate
      ) {
        setWorkflowSignModalSigner(me);
        return;
      }
    }
    addToast(
      'Não há solicitação de assinatura pendente vinculada ao seu usuário neste documento. Verifique os signatários ou o convite recebido por e-mail.',
      'warning'
    );
  };

  const handleOpenRename = () => {
    if (!selectedNode || !canRenameNode(selectedNode)) return;
    setRenameDraft(selectedNode.title);
    setShowRenameModal(true);
  };

  const handleConfirmRename = async () => {
    if (!selectedNode || renameSubmitting) return;
    const trimmed = renameDraft.trim();
    if (!trimmed) {
      addToast('O nome não pode ser vazio.', 'error');
      return;
    }
    if (trimmed === selectedNode.title.trim()) {
      setShowRenameModal(false);
      return;
    }
    setRenameSubmitting(true);
    try {
      const dto = await documentService.rename(selectedNode.id, trimmed);
      setShowRenameModal(false);
      onNodeSelect(mergeNodeWithRenameDto(selectedNode, dto));
      onDocumentUpdated(selectedNode.id, dto.path);
    } catch (error: unknown) {
      const ax = error as { response?: { status?: number; data?: { message?: string } } };
      const status = ax.response?.status;
      const msg = ax.response?.data?.message;
      if (status === 400 || status === 409) {
        addToast(
          msg || (status === 409 ? 'Já existe um item com este nome no mesmo diretório.' : 'Nome inválido.'),
          'error'
        );
      } else if (msg) {
        addToast(msg, 'error');
      } else {
        addToast('Não foi possível renomear o item.', 'error');
      }
    } finally {
      setRenameSubmitting(false);
    }
  };

  // Funções para mostrar modal de ação bloqueada
  const showActionBlocked = (actionName: string) => {
    setBlockedActionName(actionName);
    setShowActionBlockedModal(true);
  };

  const handleOpenDocument = () => {
    showActionBlocked('Abrir');
  };


  // Remove a função confirmDelete que não é mais necessária
  // const confirmDelete = () => {
  //   onDeleteDocument(selectedNode);
  //   setShowDeleteModal(false);
  // };

  // Função auxiliar para truncar texto com reticências se exceder o limite
  const truncateText = (text: string, maxLength: number = 30): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderBreadcrumb = (node: DocumentNode) => {
    const pathParts = node.path.split('/').filter(Boolean);
    const breadcrumbs = pathParts.map((part, index) => {
      const path = '/' + pathParts.slice(0, index + 1).join('/');
      return {
        name: part,
        path,
      };
    });

    return (
      <nav className="flex mb-4" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <button
              onClick={async () => {
                setIsLoadingBreadcrumb(true);
                try {
                  // Buscar dados reais da raiz
                  const nodeData = await documentService.getDocumentWithChildren('/');
                  onNodeSelect(nodeData);
                } catch (error) {
                  // Fallback: criar nó básico se falhar
                  onNodeSelect({
                    id: 'root',
                    customerId: '',
                    title: 'documentos',
                    description: '',
                    nodeType: 'folder',
                    parentDocumentId: null,
                    level: 0,
                    order: 0,
                    children: [],
                    countChild: 0,
                    path: '/',
                    fullPath: '/',
                    status: '',
                    currentVersion: null,
                    content: null
                  });
                } finally {
                  setIsLoadingBreadcrumb(false);
                }
              }}
              className={`flex items-center text-gray-500 hover:text-gray-700 ${isLoadingBreadcrumb ? 'opacity-50 cursor-wait' : ''}`}
              disabled={isLoadingBreadcrumb}
            >
              {isLoadingBreadcrumb ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
                  documentos
                </>
              ) : (
                'documentos'
              )}
            </button>
          </li>
          {breadcrumbs.map((crumb, index) => {
            const isCurrentPage = index === breadcrumbs.length - 1;

            const navigateToCrumb = async () => {
              setIsLoadingBreadcrumb(true);
              try {
                const nodeData = await documentService.getDocumentWithChildren(crumb.path);

                const node: DocumentNode = {
                  id: nodeData.id || crumb.path,
                  customerId: nodeData.customerId || '',
                  title: nodeData.title || crumb.name,
                  description: nodeData.description || '',
                  nodeType: (nodeData.nodeType as 'link' | 'folder' | 'document' | 'file' | 'edoc') || 'folder',
                  parentDocumentId: nodeData.parentDocumentId || null,
                  level: nodeData.level || 0,
                  order: nodeData.order || 0,
                  children: nodeData.children || [],
                  countChild: nodeData.countChild || 0,
                  path: crumb.path,
                  fullPath: nodeData.fullPath || crumb.path,
                  status: nodeData.status || '',
                  currentVersion: nodeData.currentVersion || null,
                  content: nodeData.content || null
                };
                onNodeSelect(node);
              } catch (error) {
                const node: DocumentNode = {
                  id: crumb.path,
                  customerId: '',
                  title: crumb.name,
                  description: '',
                  nodeType: 'folder',
                  parentDocumentId: null,
                  level: 0,
                  order: 0,
                  children: [],
                  countChild: 0,
                  path: crumb.path,
                  fullPath: crumb.path,
                  status: '',
                  currentVersion: null,
                  content: null
                };
                onNodeSelect(node);
              } finally {
                setIsLoadingBreadcrumb(false);
              }
            };

            return (
              <li key={crumb.path} className="flex items-center">
                <ChevronRightIcon className="h-4 w-4 text-gray-400 mx-2" />
                {isCurrentPage ? (
                  <span
                    className="flex items-center text-gray-900 font-medium"
                    aria-current="page"
                  >
                    {truncateText(crumb.name)}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void navigateToCrumb()}
                    className={`flex items-center text-gray-500 hover:text-gray-700 ${
                      isLoadingBreadcrumb ? 'opacity-50 cursor-wait' : ''
                    }`}
                    disabled={isLoadingBreadcrumb}
                  >
                    {isLoadingBreadcrumb ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
                        {truncateText(crumb.name)}
                      </>
                    ) : (
                      truncateText(crumb.name)
                    )}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  };

  // Função para determinar o rating atual do documento
  const getCurrentDocumentRating = (documentId: string, documentEvaluation: number | null | undefined): 'like' | 'dislike' | null => {
    // Se existe uma entrada no estado local (incluindo null), usar ela
    if (documentId in documentRatings) {
      return documentRatings[documentId];
    }
    // Senão, converter o evaluation do documento para rating
    if (documentEvaluation === 1) return 'like';
    if (documentEvaluation === 0) return 'dislike';
    return null;
  };

  // Função para avaliar documento
  const handleDocumentRating = async (documentId: string, rating: 'like' | 'dislike') => {
    // Adicionar documento ao estado de loading
    setEvaluatingDocuments(prev => new Set(prev).add(documentId));

    try {
      // Encontrar o documento para obter o evaluation atual
      const document = selectedNode;
      if (!document) {
        addToast('Erro: documento não encontrado.', 'error');
        return;
      }

      // Determinar o valor da avaliação atual
      const currentRating = getCurrentDocumentRating(documentId, document.evaluation);
      let evaluationValue: number | null = null;

      if (currentRating === rating) {
        // Se clicou no mesmo botão, remove a avaliação (null)
        evaluationValue = null;
      } else {
        // Se clicou em um botão diferente, define a nova avaliação
        evaluationValue = rating === 'like' ? 1 : 0;
      }

      // Chamar a API
      await documentService.evaluateDocument(documentId, evaluationValue);

      // Atualizar o estado local apenas se a API foi bem-sucedida
      setDocumentRatings(prev => {
        if (evaluationValue === null) {
          return { ...prev, [documentId]: null };
        }
        return { ...prev, [documentId]: rating };
      });

      // Mostrar notificação de sucesso
      const successMessage = evaluationValue === null 
        ? 'Avaliação removida' 
        : `Avaliação registrada: ${rating === 'like' ? 'Gostei' : 'Não gostei'}`;
      
      addToast(successMessage, 'success');
    } catch (error) {
      addToast('Erro ao registrar avaliação. Tente novamente.', 'error');
    } finally {
      // Remover documento do estado de loading
      setEvaluatingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  // Função para determinar se o documento é favorito
  const getCurrentFavoriteStatus = (documentId: string, documentIsFavorite: boolean | undefined): boolean => {
    // Se existe uma entrada no estado local, usar ela
    if (documentId in documentFavorites) {
      return documentFavorites[documentId];
    }
    // Senão, usar o status do documento
    return documentIsFavorite || false;
  };

  // Função para gerenciar favoritos
  const handleFavoriteToggle = async (documentId: string) => {
    // Adicionar documento ao estado de loading
    setFavoritingDocuments(prev => new Set(prev).add(documentId));

    try {
      // Encontrar o documento para obter o status atual
      const document = selectedNode;
      if (!document) {
        addToast('Erro: documento não encontrado.', 'error');
        return;
      }

      // Determinar o status atual
      const currentFavoriteStatus = getCurrentFavoriteStatus(documentId, document.isFavorite);

      if (currentFavoriteStatus) {
        // Se é favorito, remover dos favoritos
        await documentService.removeFromFavorites(documentId);
        setDocumentFavorites(prev => ({ ...prev, [documentId]: false }));
        addToast('Documento removido dos favoritos', 'success');
      } else {
        // Se não é favorito, adicionar aos favoritos
        await documentService.addToFavorites(documentId);
        setDocumentFavorites(prev => ({ ...prev, [documentId]: true }));
        addToast('Documento adicionado aos favoritos', 'success');
      }
    } catch (error) {
      addToast('Erro ao gerenciar favorito. Tente novamente.', 'error');
    } finally {
      // Remover documento do estado de loading
      setFavoritingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const renderNodeContent = (node: DocumentNode) => {
    if (node.nodeType === 'folder') {
      return (
        <div className="space-y-4">
          {renderBreadcrumb(node)}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FolderIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">{node.title}</h2>
            </div>
            <Menu as="div" className="relative">
              <Menu.Button className="p-2 hover:bg-gray-100 rounded-full">
                <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    {permissions.canDelete && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => onDeleteDocument(node)}
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } block w-full text-left px-4 py-2 text-sm text-red-600`}
                          >
                            Excluir
                          </button>
                        )}
                      </Menu.Item>
                    )}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
          {/* Só mostra o conteúdo de pasta se realmente for uma pasta */}
          {node.nodeType === 'folder' && (
            <>
              {node.children && node.children.length > 0 ? (
                <div className="space-y-4">
                  {/* Cabeçalho da listagem */}
                  <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                      {node.children.length} {node.children.length === 1 ? 'item' : 'itens'}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <button 
                        className={`p-2 ${viewMode === 'list' ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'} rounded-md hover:bg-gray-100`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewMode('list');
                        }}
                        title="Visualização em lista"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                      </button>
                      <button 
                        className={`p-2 ${viewMode === 'grid' ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'} rounded-md hover:bg-gray-100`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewMode('grid');
                        }}
                        title="Visualização em grid"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Visualização de documentos (grid ou lista) */}
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {node.children.map((child) => (
                      <div
                        key={child.id}
                        className="group relative bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                        onClick={() => onNodeSelect(child)}
                      >
                        {/* Card do documento */}
                        <div className="p-4">
                          {/* Ícone e tipo */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              {child.nodeType === 'folder' ? (
                                <div className="p-2 bg-blue-50 rounded-lg">
                                  <FolderIcon className="h-6 w-6 text-blue-600" />
                                </div>
                              ) : child.nodeType === 'edoc' ? (
                                <div className="p-2 bg-green-50 rounded-lg">
                                  <DocumentIcon className="h-6 w-6 text-green-600" />
                                </div>
                              ) : (
                                (() => {
                                  const fileName = child.metadata?.fileName || child.title || '';
                                  const ext = fileName.split('.').pop()?.toLowerCase() || '';
                                  return (
                                    <div className="p-2 bg-gray-50 rounded-lg">
                                      <span className="text-2xl">{getFileTypeIcon(ext)}</span>
                                    </div>
                                  );
                                })()
                              )}
                            </div>
                            
                            {/* Menu de ações */}
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <Menu
                                as="div"
                                className="relative"
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              >
                                <Menu.Button
                                  className="p-1 hover:bg-gray-100 rounded-md"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  <EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
                                </Menu.Button>
                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform opacity-0 scale-95"
                                  enterTo="transform opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform opacity-100 scale-100"
                                  leaveTo="transform opacity-0 scale-95"
                                >
                                  <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                                    <div className="py-1">
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onNodeSelect(child);
                                            }}
                                            className={`${
                                              active ? 'bg-gray-100' : ''
                                            } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                                          >
                                            Abrir
                                          </button>
                                        )}
                                      </Menu.Item>
                                      {child.nodeType !== 'folder' && permissions.canView && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                void handleDownloadChild(child);
                                              }}
                                              disabled={downloadingChildId === child.id}
                                              className={`${
                                                active ? 'bg-gray-100' : ''
                                              } block w-full text-left px-4 py-2 text-sm text-gray-700 disabled:opacity-50`}
                                            >
                                              {downloadingChildId === child.id ? 'Baixando...' : 'Baixar'}
                                            </button>
                                          )}
                                        </Menu.Item>
                                      )}
                                      {permissions.canDelete && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteDocument(child);
                                              }}
                                              className={`${
                                                active ? 'bg-gray-100' : ''
                                              } block w-full text-left px-4 py-2 text-sm text-red-600`}
                                            >
                                              Excluir
                                            </button>
                                          )}
                                        </Menu.Item>
                                      )}
                                    </div>
                                  </Menu.Items>
                                </Transition>
                              </Menu>
                            </div>
                          </div>
                          
                          {/* Título do documento */}
                          <div className="mb-3">
                            <h4 className="font-medium text-gray-900 text-sm leading-tight group-hover:text-blue-600 transition-colors duration-200" style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {child.title}
                            </h4>
                          </div>
                          
                          {/* Metadados do documento */}
                          <div className="space-y-2">
                            {/* Tipo do documento */}
                            <div className="flex items-center text-xs text-gray-500">
                              <span className="capitalize">
                                {child.nodeType === 'folder' ? 'Pasta' : 
                                 child.nodeType === 'edoc' ? 'Documento Eletrônico' : 
                                 'Arquivo'}
                              </span>
                            </div>
                            
                            {/* Data de modificação */}
                            {child.metadata?.updatedAt && (
                              <div className="flex items-center text-xs text-gray-500">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {child.metadata.updatedAt}
                              </div>
                            )}
                            
                            {/* Tamanho do arquivo (se aplicável) */}
                            {child.metadata?.size && child.nodeType !== 'folder' && (
                              <div className="flex items-center text-xs text-gray-500">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {child.metadata.size}
                              </div>
                            )}
                            
                            {/* Status do documento (se aplicável) */}
                            {child.status && child.nodeType !== 'folder' && (
                              <div className="flex items-center">
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getWorkflowStatusBadgeClasses(
                                    child.status
                                  )}`}
                                >
                                  {getWorkflowStatusLabel(child.status)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Indicador de hover */}
                        <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-200 rounded-xl transition-colors duration-200 pointer-events-none" />
                      </div>
                    ))}
                  </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {node.children.map((child) => (
                        <div
                          key={child.id}
                          className="group py-4 flex items-center hover:bg-gray-50 cursor-pointer px-4 rounded-md transition-colors duration-200"
                          onClick={() => onNodeSelect(child)}
                        >
                          {/* Ícone do documento */}
                          <div className="flex-shrink-0 mr-4">
                            {child.nodeType === 'folder' ? (
                              <div className="p-2 bg-blue-50 rounded-lg">
                                <FolderIcon className="h-6 w-6 text-blue-600" />
                              </div>
                            ) : child.nodeType === 'edoc' ? (
                              <div className="p-2 bg-green-50 rounded-lg">
                                <DocumentIcon className="h-6 w-6 text-green-600" />
                              </div>
                            ) : (
                              (() => {
                                const fileName = child.metadata?.fileName || child.title || '';
                                const ext = fileName.split('.').pop()?.toLowerCase() || '';
                                return (
                                  <div className="p-2 bg-gray-50 rounded-lg">
                                    <span className="text-2xl">{getFileTypeIcon(ext)}</span>
                                  </div>
                                );
                              })()
                            )}
                          </div>
                          
                          {/* Informações do documento */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors duration-200">
                              {child.title}
                            </h4>
                            <div className="flex items-center mt-1">
                              <span className="text-xs text-gray-500 capitalize">
                                {child.nodeType === 'folder' ? 'Pasta' : 
                                 child.nodeType === 'edoc' ? 'Documento Eletrônico' : 
                                 'Arquivo'}
                              </span>
                              {child.metadata?.updatedAt && (
                                <>
                                  <span className="mx-2 text-gray-300">•</span>
                                  <span className="text-xs text-gray-500">{child.metadata.updatedAt}</span>
                                </>
                              )}
                              {child.metadata?.size && child.nodeType !== 'folder' && (
                                <>
                                  <span className="mx-2 text-gray-300">•</span>
                                  <span className="text-xs text-gray-500">
                                  {child.metadata.size}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Status do documento (se aplicável) */}
                          {child.status && child.nodeType !== 'folder' && (
                            <div className="ml-4">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getWorkflowStatusBadgeClasses(
                                  child.status
                                )}`}
                              >
                                {getWorkflowStatusLabel(child.status)}
                              </span>
                            </div>
                          )}
                          
                          {/* Menu de ações */}
                          <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <Menu
                              as="div"
                              className="relative"
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                            >
                              <Menu.Button
                                className="p-1 hover:bg-gray-100 rounded-md"
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              >
                                <EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
                              </Menu.Button>
                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                                  <div className="py-1">
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onNodeSelect(child);
                                          }}
                                          className={`${
                                            active ? 'bg-gray-100' : ''
                                          } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                                        >
                                          Abrir
                                        </button>
                                      )}
                                    </Menu.Item>
                                    {child.nodeType !== 'folder' && permissions.canView && (
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              void handleDownloadChild(child);
                                            }}
                                            disabled={downloadingChildId === child.id}
                                            className={`${
                                              active ? 'bg-gray-100' : ''
                                            } block w-full text-left px-4 py-2 text-sm text-gray-700 disabled:opacity-50`}
                                          >
                                            {downloadingChildId === child.id ? 'Baixando...' : 'Baixar'}
                                          </button>
                                        )}
                                      </Menu.Item>
                                    )}
                                    {permissions.canDelete && (
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onDeleteDocument(child);
                                            }}
                                            className={`${
                                              active ? 'bg-gray-100' : ''
                                            } block w-full text-left px-4 py-2 text-sm text-red-600`}
                                          >
                                            Excluir
                                          </button>
                                        )}
                                      </Menu.Item>
                                    )}
                                  </div>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <FolderIcon className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Esta pasta está vazia</h3>
                  <p className="text-gray-500 mb-6">Comece criando documentos ou fazendo upload de arquivos.</p>
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={onNewDocument}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Novo Documento
                    </button>
                    <button
                      onClick={onNewFolder}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <FolderPlusIcon className="h-4 w-4 mr-2" />
                      Nova Pasta
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {renderBreadcrumb(node)}
        
        {/* Cabeçalho do documento */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {(() => {
              if (node.nodeType === 'edoc') {
                return <DocumentIcon className="h-6 w-6 text-gray-400 mr-2" />;
              }
              const fileName = node.metadata?.fileName || node.title || '';
              const ext = fileName.split('.').pop()?.toLowerCase() || '';
              return <span className="mr-2">{getFileTypeIcon(ext)}</span>;
            })()}
            <div className="flex items-center space-x-3">
              <h2 className="text-lg font-medium text-gray-900">{node.title}</h2>
              {/* Status do documento */}
              {(() => {
                const status = statusForNode(node);
                return (
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getWorkflowStatusBadgeClasses(
                        status
                      )}`}
                    >
                      {getWorkflowStatusLabel(status)}
                    </span>
                    {isPublicationExpired && (
                      <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                        Expirado
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          <Menu as="div" className="relative">
            <Menu.Button className="p-2 hover:bg-gray-100 rounded-full">
              <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleOpenDocument}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                      >
                        Abrir
                      </button>
                    )}
                  </Menu.Item>
                  
                  {/* Botão de Editar - documentos eletrônicos, com permissão e sem assinatura bloqueante */}
                  {isElectronicDocument(node) &&
                    permissions.canEdit &&
                    !hasBlockingSignatureRequest(node) && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => onEditDocument(node)}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                        >
                          Editar
                        </button>
                      )}
                    </Menu.Item>
                  )}

                  {isElectronicDocument(node) && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setShowVersionHistoryModal(true)}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                        >
                          Histórico de versões
                        </button>
                      )}
                    </Menu.Item>
                  )}

                  {canRenameNode(node) && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleOpenRename}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                        >
                          Renomear
                        </button>
                      )}
                    </Menu.Item>
                  )}

                  {canDuplicateDocument(node) && onDuplicateDocument && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => void handleDuplicate()}
                          disabled={duplicateSubmitting}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-2 text-sm text-gray-700 disabled:opacity-50`}
                        >
                          {duplicateSubmitting ? 'Duplicando...' : 'Duplicar'}
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  
                  {permissions.canDelete && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => onDeleteDocument(node)}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-2 text-sm text-red-600`}
                        >
                          Excluir
                        </button>
                      )}
                    </Menu.Item>
                  )}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>

        {/* Metadados do documento */}
        {node.metadata && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Criado por</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {node.metadata.author || node.metadata.createdBy || 'Sistema'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Data de criação</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {node.metadata.createdAt || ''}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Última modificação</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {node.metadata.updatedAt || node.metadata.lastModifiedAt || ''}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Modificado por</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {node.metadata.updater || node.metadata.lastModifiedBy || 'Sistema'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Versão</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {node.currentVersion || '1.0'}
                </p>
              </div>
              {statusForNode(node) === 'published' && publicationExpiresAt && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Validade da publicação</h3>
                  <p className={`mt-1 text-sm ${isPublicationExpired ? 'text-red-700 font-medium' : 'text-gray-900'}`}>
                    {formatPublicationExpiration(publicationExpiresAt)}
                  </p>
                </div>
              )}
              {standaloneSignatureExpiresAt && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Prazo para assinatura</h3>
                  <p className={`mt-1 text-sm ${isSignatureDeadlineExpired ? 'text-red-700 font-medium' : 'text-gray-900'}`}>
                    {formatPublicationExpiration(standaloneSignatureExpiresAt)}
                  </p>
                </div>
              )}
              {documentWorkflow && (workflowTemplate || documentWorkflow.workflowId) && !workflowUiLoading && (
                <div className="col-span-2">
                  <h3 className="text-sm font-medium text-gray-500">Workflow</h3>
                  <p className="mt-1 text-sm text-gray-900">
                    Workflow: {workflowTemplate?.name ?? documentWorkflow.workflowId} (Origem:{' '}
                    {translateDocumentWorkflowSource(documentWorkflow.source)})
                  </p>
                </div>
              )}
              {documentWorkflow &&
                (workflowTemplate || documentWorkflow.workflowId) &&
                !workflowUiLoading &&
                isWorkflowCapableNode(node) &&
                (canWorkflowPublish(node) || canArchiveWithWorkflow(node)) && (
                  <div className="col-span-2 border-t border-gray-100 pt-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1">
                          <ClockIcon className="h-4 w-4 shrink-0" aria-hidden />
                          Agendamento automático
                        </h3>
                        <p className="mt-1 text-xs text-gray-600">
                          Publicação:{' '}
                          <span className="font-medium text-gray-900">
                            {formatLifecycleDateHint(lifecycleSchedule.publish)}
                          </span>
                          {' · '}
                          Arquivo:{' '}
                          <span className="font-medium text-gray-900">
                            {formatLifecycleDateHint(lifecycleSchedule.archive)}
                          </span>
                        </p>
                        {lifecycleSchedule.publish &&
                          lifecycleSchedule.archive &&
                          new Date(lifecycleSchedule.archive).getTime() <=
                            new Date(lifecycleSchedule.publish).getTime() && (
                            <p className="mt-1 text-xs text-amber-700">
                              A data de arquivo não é posterior à de publicação; confirme se corresponde ao
                              pretendido.
                            </p>
                          )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowLifecycleScheduleModal(true)}
                        className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      >
                        Editar agendamento
                      </button>
                    </div>
                  </div>
                )}
              {postPublishBlockedBySignature && !documentSignatureBanner && selectedNode && (
                <div className="col-span-2 border-t border-gray-100 pt-3">
                  <p className="text-sm text-amber-800">
                    Arquivar e nova versão ficam disponíveis após concluir as assinaturas exigidas deste documento.
                  </p>
                </div>
              )}
              {documentSignatureBanner && (
                <div className="col-span-2 border-t border-gray-100 pt-3">
                  <h3 className="text-sm font-medium text-gray-500">
                    {documentSignatureBanner.source === 'workflow'
                      ? 'Assinatura no workflow'
                      : 'Assinatura digital'}
                  </h3>
                  {documentSignatureBanner.kind === 'completed' && (
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-emerald-900">
                      <span>
                        {documentSignatureBanner.source === 'workflow'
                          ? 'Assinatura concluída neste fluxo.'
                          : 'Assinatura concluída nesta versão publicada.'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowSignersModal(true)}
                        className="font-medium text-indigo-600 hover:underline"
                      >
                        Ver signatários e status
                      </button>
                    </div>
                  )}
                  {documentSignatureBanner.kind === 'pending_send' && (
                    <p className="mt-1 text-sm text-gray-700">
                      {documentSignatureBanner.source === 'workflow'
                        ? 'Solicitação pendente — use a ação do fluxo Enviar para assinatura ou notifique os signatários conforme o processo.'
                        : 'Solicitação criada — use o botão Enviar para assinatura para notificar os signatários.'}
                    </p>
                  )}
                  {postPublishBlockedBySignature && (
                    <p className="mt-2 text-sm text-amber-800">
                      Arquivar e nova versão ficam disponíveis após concluir as assinaturas exigidas deste documento.
                    </p>
                  )}
                  {documentSignatureBanner.kind === 'in_progress' && (
                    <p className="mt-1 text-sm text-gray-700">
                      Assinaturas: {documentSignatureBanner.signed} de {documentSignatureBanner.total}{' '}
                      concluída(s).{' '}
                      <button
                        type="button"
                        onClick={() => setShowSignersModal(true)}
                        className="font-medium text-indigo-600 hover:underline"
                      >
                        Ver detalhes
                      </button>
                    </p>
                  )}
                  {documentSignatureBanner.kind === 'cancelled' && (
                    <p className="mt-1 text-sm text-amber-800">
                      A última solicitação de assinatura foi cancelada. Você pode abrir uma nova solicitação se
                      ainda for necessário.
                    </p>
                  )}
                </div>
              )}
              {node.nodeType === 'document' || node.nodeType === 'file' || node.nodeType === 'edoc' || node.nodeType === 'link' ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Tamanho</h3>
                  <p className="mt-1 text-sm text-gray-900">{node.metadata.size || 'N/A'}</p>
                </div>
              ) : null}
              <div>
                <h3 className="text-sm font-medium text-gray-500">Tipo de conteúdo</h3>
                <p className="mt-1 text-gray-900">{node.metadata.contentType || 'N/A'}</p>
              </div>
            </div>
            
            {/* Campo Descrição */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Descrição</h3>
                {!permissions.canEdit && (
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                    Somente leitura
                  </span>
                )}
              </div>
              <div className="relative">
                {permissions.canEdit ? (
                  <textarea
                    value={documentDescription}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    placeholder="Adicione uma descrição para este documento..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                    rows={2}
                  />
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600 min-h-[48px] flex items-center">
                    {documentDescription || 'Nenhuma descrição disponível'}
                  </div>
                )}
                {isSavingDescription && (
                  <div className="absolute top-2 right-2 flex items-center text-xs text-gray-500">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500 mr-1"></div>
                    Salvando...
                  </div>
                )}
              </div>
            </div>
            
            {/* Categorias e Tags - Lado a lado */}
            {permissions.canEdit && selectedNode && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Categorias */}
                  <div className="max-w-[300px]">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Categorias</h3>
                    <DocumentCategoriesAndTags
                      documentId={node.id}
                      currentCategory={documentCategory}
                      currentCategories={documentCategories}
                      currentTags={documentTags}
                      onCategoryChange={handleCategoryChange}
                      onCategoriesChange={handleCategoriesChange}
                      onTagsChange={handleTagsChange}
                      showOnlyCategories={true}
                    />
                  </div>
                  
                  {/* Tags */}
                  <div className="max-w-[300px]">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Tags</h3>
                    <DocumentCategoriesAndTags
                      documentId={node.id}
                      currentCategory={documentCategory}
                      currentCategories={documentCategories}
                      currentTags={documentTags}
                      onCategoryChange={handleCategoryChange}
                      onCategoriesChange={handleCategoriesChange}
                      onTagsChange={handleTagsChange}
                      showOnlyTags={true}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Documentos Relacionados */}
            {selectedNode && selectedNode.nodeType !== 'folder' && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                    <LinkIcon className="h-4 w-4" />
                    Documentos Relacionados
                  </h3>
                  {permissions.canEdit && (
                    <button
                      onClick={() => setShowRelatedModal(true)}
                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                    >
                      <PlusIcon className="h-3.5 w-3.5 mr-1" />
                      Relacionar documento
                    </button>
                  )}
                </div>

                {loadingRelated ? (
                  <div className="flex items-center text-xs text-gray-400 py-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
                    Carregando...
                  </div>
                ) : relatedDocuments.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Nenhum documento relacionado</p>
                ) : (
                  <ul className="space-y-2">
                    {relatedDocuments.map(rel => {
                      const isSource = rel.sourceDocumentId === selectedNode?.id;
                      const otherTitle = isSource ? rel.targetDocumentTitle : rel.sourceDocumentTitle;
                      const otherId = isSource ? rel.targetDocumentId : rel.sourceDocumentId;
                      const directionHint = getRelationshipDirectionHint(rel.relationshipType, isSource);
                      const arrowSymbol = isSource ? '→' : '←';
                      const arrowTooltip = isSource
                        ? 'Vínculo criado a partir deste documento'
                        : 'Vínculo criado a partir do documento relacionado';
                      return (
                      <li key={rel.id} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 group">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <DocumentIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span
                              className="text-[11px] text-gray-400 flex-shrink-0 select-none"
                              title={arrowTooltip}
                            >
                              {arrowSymbol}
                            </span>
                            <button
                              onClick={() => navigate(`/documents/${otherId}/details`)}
                              className="text-sm text-blue-600 hover:underline truncate"
                              title={otherTitle}
                            >
                              {otherTitle}
                            </button>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${getRelationshipBadgeColor(rel.relationshipTypeIsSystem)}`}
                              title={directionHint ?? undefined}
                            >
                              {rel.relationshipType}
                            </span>
                          </div>
                          {directionHint && (
                            <p className="text-xs text-slate-600 mt-0.5 ml-6 leading-snug" title={directionHint}>
                              {directionHint}
                            </p>
                          )}
                          {rel.description && (
                            <p className="text-xs text-gray-500 mt-0.5 ml-6 truncate" title={rel.description}>{rel.description}</p>
                          )}
                        </div>
                        {permissions.canEdit && (
                          <button
                            onClick={() => handleRemoveRelationship(rel.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0 inline-flex items-center justify-center h-6 w-6 rounded text-red-500 hover:bg-red-100"
                            title="Remover relacionamento"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {node.id !== 'root' && (
              <div className="border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowDocumentAuditHistoryModal(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 sm:w-auto"
                >
                  <ClipboardDocumentListIcon className="h-5 w-5 shrink-0 text-gray-500" aria-hidden />
                  Auditoria
                </button>
              </div>
            )}

            {/* Ações do documento */}
            {shouldShowActionsSection(node) && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Ações</h3>
                <div className="flex flex-wrap gap-2">
                  {/* Botão de Indexação Semântica - só aparece para documentos que podem ser indexados e se o usuário for admin */}
                  {canBeIndexedInElasticsearch(node) && user?.isAdmin && (
                    <div className="relative group">
                      <button
                        onClick={handleSemanticIndexing}
                        disabled={isLoadingIndexing}
                        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                          isIndexedInElasticsearch
                            ? 'border-red-200 text-red-700 bg-red-50 hover:bg-red-100'
                            : 'border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100'
                        }`}
                      >
                        {isLoadingIndexing ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1.5"></div>
                        ) : isIndexedInElasticsearch ? (
                          <MagnifyingGlassMinusIcon className="h-3 w-3 mr-1.5" />
                        ) : (
                          <MagnifyingGlassIcon className="h-3 w-3 mr-1.5" />
                        )}
                        {isIndexedInElasticsearch ? 'Remover da Busca' : 'Indexar na Busca'}
                      </button>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                        {isIndexedInElasticsearch 
                          ? 'Remove o documento do índice da busca' 
                          : 'Adiciona o documento ao índice da busca'
                        }
                      </div>
                    </div>
                  )}
                  
                  {/* Botão de Chatbot - só aparece para documentos que podem ser adicionados ao chatbot e se tiver permissão de visualização */}
                  {canBeAddedToChatbot(node) && permissions.canView && (
                    <div className="relative group">
                      <button
                        onClick={() => setShowManageChatbotsModal(true)}
                        disabled={chatbotLoadingDocuments.has(node.id)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 transition-colors disabled:opacity-50"
                      >
                        {chatbotLoadingDocuments.has(node.id) ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1.5"></div>
                        ) : (
                          <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1.5" />
                        )}
                        {isInChatbot ? 'Gerenciar Chatbots' : 'Adicionar ao Chatbot'}
                      </button>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                        {isInChatbot 
                          ? 'Adicionar ou remover este documento de chatbots' 
                          : 'Adicionar este documento a um ou mais chatbots'
                        }
                      </div>
                    </div>
                  )}
                  
                  {/* Botões de Avaliação - só para documentos publicados e com permissão de visualização */}
                  {statusForNode(node) === 'published' && permissions.canView && (
                    <>
                      {/* Botão Like */}
                      <div className="relative group">
                        <button
                          onClick={() => handleDocumentRating(node.id, 'like')}
                          disabled={evaluatingDocuments.has(node.id)}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                            getCurrentDocumentRating(node.id, node.evaluation) === 'like'
                              ? 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                              : 'border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100'
                          } ${evaluatingDocuments.has(node.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {evaluatingDocuments.has(node.id) ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1.5"></div>
                          ) : (
                            <HandThumbUpIcon className="h-3 w-3 mr-1.5" />
                          )}
                          Gostei
                        </button>
                        
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                          {getCurrentDocumentRating(node.id, node.evaluation) === 'like' 
                            ? 'Remover avaliação positiva' 
                            : 'Avaliar positivamente este documento'
                          }
                </div>
              </div>
                      
                      {/* Botão Dislike */}
                      <div className="relative group">
                        <button
                          onClick={() => handleDocumentRating(node.id, 'dislike')}
                          disabled={evaluatingDocuments.has(node.id)}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                            getCurrentDocumentRating(node.id, node.evaluation) === 'dislike'
                              ? 'border-red-200 text-red-700 bg-red-50 hover:bg-red-100'
                              : 'border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100'
                          } ${evaluatingDocuments.has(node.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {evaluatingDocuments.has(node.id) ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1.5"></div>
                          ) : (
                            <HandThumbDownIcon className="h-3 w-3 mr-1.5" />
                          )}
                          Não Gostei
                        </button>
                        
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                          {getCurrentDocumentRating(node.id, node.evaluation) === 'dislike' 
                            ? 'Remover avaliação negativa' 
                            : 'Avaliar negativamente este documento'
                          }
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Botão de Favorito - só com permissão de visualização */}
                  {permissions.canView && (
                    <div className="relative group">
                    <button
                      onClick={() => handleFavoriteToggle(node.id)}
                      disabled={favoritingDocuments.has(node.id)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                        getCurrentFavoriteStatus(node.id, node.isFavorite)
                          ? 'border-yellow-200 text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                          : 'border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100'
                      } ${favoritingDocuments.has(node.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {favoritingDocuments.has(node.id) ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1.5"></div>
                      ) : (
                        <StarIcon className={`h-3 w-3 mr-1.5 ${getCurrentFavoriteStatus(node.id, node.isFavorite) ? 'fill-current' : ''}`} />
                      )}
                      Favorito
                    </button>
                    
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                      {getCurrentFavoriteStatus(node.id, node.isFavorite) 
                        ? 'Remover este documento dos favoritos' 
                        : 'Adicionar este documento aos favoritos'
                      }
                    </div>
                  </div>
                  )}

                  {/* Comentários: revisão, assinatura ou publicado (conforme fluxo) */}
                  {permissions.canView &&
                    ['review', 'signature', 'published'].includes(statusForNode(node)) && (
                    <div className="relative group">
                      <button
                        onClick={() => setCommentsModalOpen(true)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <CommentIcon className="h-3 w-3 mr-1.5" />
                        Comentários
                      </button>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                        Ver e adicionar comentários
                      </div>
                    </div>
                  )}
                </div>

                {/* Chatbots aos quais o documento está vinculado */}
                {canBeAddedToChatbot(node) && permissions.canView && isInChatbot && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-500 mb-1.5">
                      {linkedChatbots.length === 1 ? 'Vinculado ao chatbot' : 'Vinculado aos chatbots'}
                    </p>
                    {linkedChatbotsLoading ? (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                        Carregando...
                      </div>
                    ) : linkedChatbots.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {linkedChatbots.map((chatbot) => (
                          <button
                            key={chatbot.id}
                            type="button"
                            onClick={() => {
                              const params = new URLSearchParams({ workspace: chatbot.id });
                              const storageCustomerId = getCustomerIdFromStorage();
                              const tenantId =
                                contextTenantId?.trim() || documentService.getActiveTenantId()?.trim();
                              if (tenantId && tenantId !== storageCustomerId)
                              {
                                params.set('tenant', tenantId);
                              }
                              navigate(`/copilot?${params.toString()}`);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors cursor-pointer"
                            title={`Abrir o chatbot ${chatbot.assistantName || chatbot.name}${chatbot.description ? ` — ${chatbot.description}` : ''}`}
                          >
                            <ChatBubbleLeftRightIcon className="h-3 w-3" />
                            {chatbot.assistantName || chatbot.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs italic text-gray-400">
                        Nenhum chatbot vinculado
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {workflowContextBanner && node.id !== 'root' && (
          <div className="rounded-lg border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white px-4 py-4 shadow-sm sm:px-5">
            {workflowContextBanner.mode === 'loading' && (
              <div className="h-24 animate-pulse rounded-md bg-indigo-100/50" aria-hidden />
            )}
            {workflowContextBanner.mode === 'unknown' && (
              <p className="text-sm text-amber-900">
                Não foi possível identificar a etapa atual do fluxo. Atualize a página ou abra o histórico do
                workflow em <span className="font-medium">Histórico do workflow</span>.
              </p>
            )}
            {workflowContextBanner.mode === 'active' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
                      {workflowContextBanner.isEndedOverview ? 'Resumo do fluxo' : 'Etapa atual'}
                    </p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {workflowContextBanner.title}
                      {workflowContextBanner.stepPosition ? ` • ${workflowContextBanner.stepPosition}` : ''}
                    </p>
                    <p className="mt-1 text-xs text-gray-700">{workflowContextBanner.description}</p>
                    {workflowContextBanner.due && (
                      <p className="mt-1 text-xs font-medium text-indigo-900">
                        Prazo (SLA): {workflowContextBanner.due}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDocumentWorkflowHistoryModal(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4 text-gray-500" aria-hidden />
                    Ver histórico completo
                  </button>
                </div>

                <div className="overflow-x-auto pb-1">
                  <div className="flex min-w-max items-start gap-1.5">
                    {workflowContextBanner.timelineSteps.map((step, index) => {                      
                      const isDone = step.state === 'done';
                      const isCurrent = step.state === 'current';
                      return (
                        <React.Fragment key={step.id}>
                          <button
                            type="button"
                            onClick={() => setShowDocumentWorkflowHistoryModal(true)}
                            className={`group flex min-w-[96px] flex-col items-center rounded-md px-2 py-1.5 text-center transition-colors ${
                              isCurrent ? 'bg-blue-50' : 'hover:bg-indigo-50/70'
                            }`}
                            title="Abrir histórico do workflow"
                          >
                            <span
                              className={`flex h-6 w-6 items-center justify-center rounded-full border-2 text-[11px] font-semibold ${
                                isCurrent
                                  ? 'border-blue-600 bg-white text-blue-700'
                                  : isDone
                                    ? 'border-emerald-600 bg-emerald-600 text-white'
                                    : 'border-slate-300 bg-white text-slate-500'
                              }`}
                            >
                              {isDone ? '✓' : index + 1}
                            </span>
                            <span
                              className={`mt-1.5 line-clamp-1 text-xs font-semibold ${
                                isCurrent ? 'text-blue-700' : isDone ? 'text-emerald-700' : 'text-slate-600'
                              }`}
                            >
                              {step.name}
                            </span>
                            <span className="mt-0.5 text-[11px] text-slate-500">{step.dateLabel ?? '-'}</span>
                          </button>
                          {index < workflowContextBanner.timelineSteps.length - 1 && (
                            <span
                              className={`mt-3 h-0.5 w-8 rounded-full ${
                                isDone ? 'bg-emerald-500' : 'bg-slate-300'
                              }`}
                              aria-hidden
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Botões de ação */}
        {/* Só mostra a seção se houver pelo menos um botão visível */}
        {(selectedNode && isElectronicDocument(selectedNode) && permissions.canView) ||
         (selectedNode && isElectronicDocument(selectedNode) && permissions.canEdit) ||
         (selectedNode && !isElectronicDocument(selectedNode) && permissions.canView) ||
         (selectedNode && canLegacyPublishPreview(selectedNode) && onPublishDocument && !documentWorkflow) ||
         (selectedNode && canArchivePublishedOutsideWorkflow(selectedNode)) ||
         (selectedNode && canWorkflowPublish(selectedNode) && documentWorkflow) ||
         (selectedNode && canStandaloneOpenSignatureRequestModal(selectedNode)) ||
         (selectedNode && canStandaloneSendForSignature(selectedNode)) ||
         (selectedNode && documentSignatureBanner) ||
         (selectedNode && canShareDocument(selectedNode)) ||
         permissions.canManagePermissions ||
         permissions.canDelete ||
         (selectedNode && canShowAnyWorkflowTransition(selectedNode)) ? (
          <div className="border-t pt-4">
            <div className="flex gap-3 my-4">
              {/* Botão de Visualizar - para todos os tipos de documentos se tiver permissão */}
              {selectedNode && selectedNode.nodeType !== 'folder' && permissions.canView && (
                <button
                  onClick={handleViewDocument}
                  disabled={isViewing}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isViewing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Abrindo...
                    </>
                  ) : (
                    <>
                      <EyeIcon className="h-4 w-4 mr-2" />
                      Visualizar
                    </>
                  )}
                </button>
              )}

              {selectedNode &&
                canLegacyPublishPreview(selectedNode) &&
                onPublishDocument &&
                !documentWorkflow && (
                <button
                  onClick={() => setShowPublishPreview(true)}
                  disabled={isPublishBlockedBySignature(selectedNode)}
                  title={isPublishBlockedBySignature(selectedNode) ? 'Aguardando assinaturas obrigatórias' : undefined}
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isPublishBlockedBySignature(selectedNode)
                      ? 'bg-green-400 cursor-not-allowed opacity-60'
                      : 'bg-green-600 hover:bg-green-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Publicar
                </button>
              )}

              {selectedNode && canArchivePublishedOutsideWorkflow(selectedNode) && (
                <button
                  type="button"
                  onClick={() => void handleArchivePublishedOutsideWorkflow()}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-700 hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                >
                  <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                  Arquivar
                </button>
              )}

              {selectedNode && canStandaloneOpenSignatureRequestModal(selectedNode) && (
                <button
                  type="button"
                  onClick={() => setShowStandaloneSignatureRequestModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-indigo-300 rounded-md shadow-sm text-sm font-medium text-indigo-800 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                  Solicitar assinatura
                </button>
              )}

              {selectedNode && canStandaloneSendForSignature(selectedNode) && (
                <button
                  type="button"
                  onClick={() => {
                    const pendingRequest = pickPendingSignatureRequest(
                      activeSignatureRequest,
                      latestSignatureRequest
                    );
                    if (!pendingRequest)
                    {
                      return;
                    }

                    if (selectedNode.nodeType === 'edoc')
                    {
                      setPendingSignaturePlacementModal({
                        signatureRequestId: pendingRequest.id,
                        action: 'standalone',
                      });
                      return;
                    }

                    void (async () => {
                      try {
                        const updated = await documentSignatureService.sendForSignature(pendingRequest.id);
                        setActiveSignatureRequest(updated);
                        addToast('Solicitação de assinatura enviada.', 'success');
                        await refreshDocumentSignatureState(selectedNode.id);
                        onDocumentUpdated(selectedNode.id);
                      } catch (err) {
                        addToast(err instanceof Error ? err.message : 'Erro ao enviar para assinatura.', 'error');
                      }
                    })();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-indigo-300 rounded-md shadow-sm text-sm font-medium text-indigo-800 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  Enviar para assinatura
                </button>
              )}

              {selectedNode && documentWorkflow && (
                canWorkflowPublish(selectedNode) ||
                shouldShowCoherenceBlockedWorkflowAction(
                  'Publish',
                  !!currentStepRuntime?.hasOpenStep &&
                    (currentWorkflowUiStatus(selectedNode) === 'edit' || isCurrentStepPublishDefinition())
                )) && (
                <button
                  type="button"
                  disabled={
                    !!workflowActionDisabledReason('Publish') || !canWorkflowPublish(selectedNode)
                  }
                  title={
                    workflowActionDisabledReason('Publish') ??
                    (canDirectPublish() ? 'Esta etapa permite publicação direta pelo fluxo.' : undefined)
                  }
                  onClick={() => handleWorkflowAction('Publish')}
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                    workflowActionDisabledReason('Publish') || !canWorkflowPublish(selectedNode)
                      ? 'bg-green-400 text-white opacity-50 cursor-not-allowed'
                      : 'text-white bg-green-600 hover:bg-green-700'
                  }`}
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Publicar
                </button>
              )}
              
              {/* Botão de Editar - documentos eletrônicos, com permissão e sem assinatura bloqueante */}
              {selectedNode &&
                isElectronicDocument(selectedNode) &&
                permissions.canEdit &&
                !hasBlockingSignatureRequest(selectedNode) &&
                (canEditDocument() || canSaveWorkflow(selectedNode)) && (
                <button
                  onClick={() => onEditDocument(selectedNode)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Editar
                </button>
              )}
              
              {selectedNode &&
                selectedNode.nodeType !== 'folder' &&
                permissions.canView && (
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    title={
                      latestSignatureRequest &&
                      documentIdsMatch(latestSignatureRequest.documentId, selectedNode.id) &&
                      latestSignatureRequest.finalSignedDocumentFailed === true
                        ? 'A geração do PDF consolidado falhou. O download usa a prévia com assinaturas, se disponível.'
                        : latestSignatureRequest &&
                            documentIdsMatch(latestSignatureRequest.documentId, selectedNode.id) &&
                            latestSignatureRequest.finalSignedDocumentProcessing === true
                          ? 'Baixa o arquivo do documento. O PDF com todas as assinaturas incorporadas ainda está sendo gerado.'
                          : latestSignatureRequest &&
                              documentIdsMatch(latestSignatureRequest.documentId, selectedNode.id) &&
                              latestSignatureRequest.partialSignedDocumentFailed === true
                            ? 'A geração da prévia com assinaturas falhou. O download usa a última prévia disponível ou o original.'
                            : latestSignatureRequest &&
                                documentIdsMatch(latestSignatureRequest.documentId, selectedNode.id) &&
                                signatureHasCollectedSignatures(latestSignatureRequest) &&
                                latestSignatureRequest.partialSignedDocumentAvailable !== true &&
                                latestSignatureRequest.finalSignedDocumentAvailable !== true
                              ? 'Prévia com assinaturas ainda não disponível. O download usa o documento original.'
                              : undefined
                    }
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {isDownloading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    ) : (
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    )}
                    {isDownloading ? 'Baixando...' : primaryDownloadButtonLabel}
                  </button>
                )}

              {selectedNode && canShareDocument(selectedNode) && (
                <button
                  type="button"
                  onClick={() => setShowShareModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ShareIcon className="h-4 w-4 mr-2" />
                  Compartilhar
                </button>
              )}

              {selectedNode &&
                selectedNode.nodeType !== 'folder' &&
                permissions.canView &&
                (activeSignatureRequest || latestSignatureRequest) && (
                  <button
                    type="button"
                    onClick={() => setShowSignersModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Signatários
                  </button>
                )}
              
              {/* Botão de Permissões - apenas se tiver permissão para gerenciar permissões */}
              {permissions.canManagePermissions && (
                <button
                  onClick={handlePermissions}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <KeyIcon className="h-4 w-4 mr-2" />
                  Permissões
                </button>
              )}
              
              {/* Botão de Excluir - apenas se tiver permissão para excluir */}
              {permissions.canDelete && (
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Excluir
                </button>
              )}
            </div>
            <div className="flex gap-3 flex-wrap">
              {selectedNode && canShowWorkflowActions(selectedNode) && (
                canSendToReview(selectedNode) ||
                shouldShowCoherenceBlockedWorkflowAction(
                  'SendForReview',
                  !!currentStepRuntime?.hasOpenStep && currentWorkflowUiStatus(selectedNode) === 'edit'
                )) && (
                <button
                  type="button"
                  disabled={
                    !!workflowActionDisabledReason('SendForReview') || !canSendToReview(selectedNode)
                  }
                  title={workflowActionDisabledReason('SendForReview') ?? undefined}
                  onClick={() => handleWorkflowAction('SendForReview')}
                  className={`${workflowActionButtonClass(
                    !workflowActionDisabledReason('SendForReview') && !!canSendToReview(selectedNode)
                  )} border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500`}
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  Enviar para revisão
                </button>
              )}

              {selectedNode && canShowWorkflowActions(selectedNode) && (
                canApprove(selectedNode) ||
                shouldShowCoherenceBlockedWorkflowAction(
                  'Approve',
                  !!currentStepRuntime?.hasOpenStep && currentWorkflowUiStatus(selectedNode) === 'review'
                )) && (
                <button
                  type="button"
                  disabled={!!workflowActionDisabledReason('Approve') || !canApprove(selectedNode)}
                  title={workflowActionDisabledReason('Approve') ?? undefined}
                  onClick={() => handleWorkflowAction('Approve')}
                  className={`${workflowActionButtonClass(
                    !workflowActionDisabledReason('Approve') && !!canApprove(selectedNode)
                  )} border-blue-300 text-blue-700 bg-white hover:bg-blue-50 focus:ring-blue-500`}
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Aprovar
                </button>
              )}

              {selectedNode && canShowWorkflowActions(selectedNode) && (
                canSendForSignature(selectedNode) ||
                shouldShowCoherenceBlockedWorkflowAction(
                  WorkflowActionCodes.SendForSignature,
                  !!currentStepRuntime?.hasOpenStep &&
                    currentWorkflowUiStatus(selectedNode) === 'edit'
                )) && (
                <button
                  type="button"
                  disabled={
                    !!workflowActionDisabledReason(WorkflowActionCodes.SendForSignature) ||
                    !canSendForSignature(selectedNode)
                  }
                  title={workflowActionDisabledReason(WorkflowActionCodes.SendForSignature) ?? undefined}
                  onClick={() => handleWorkflowAction(WorkflowActionCodes.SendForSignature)}
                  className={`${workflowActionButtonClass(
                    !workflowActionDisabledReason(WorkflowActionCodes.SendForSignature) &&
                      !!canSendForSignature(selectedNode)
                  )} border-indigo-300 text-indigo-800 bg-white hover:bg-indigo-50 focus:ring-indigo-500`}
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  Enviar para assinatura
                </button>
              )}

              {selectedNode && canShowWorkflowActions(selectedNode) && (
                canReject(selectedNode) ||
                shouldShowCoherenceBlockedWorkflowAction(
                  'Reject',
                  !!currentStepRuntime?.hasOpenStep &&
                    (currentWorkflowUiStatus(selectedNode) === 'review' ||
                      currentWorkflowUiStatus(selectedNode) === 'signature')
                )) && (
                <button
                  type="button"
                  disabled={!!workflowActionDisabledReason('Reject') || !canReject(selectedNode)}
                  title={workflowActionDisabledReason('Reject') ?? undefined}
                  onClick={() => handleWorkflowAction('Reject')}
                  className={`${workflowActionButtonClass(
                    !workflowActionDisabledReason('Reject') && !!canReject(selectedNode)
                  )} border-yellow-300 text-yellow-800 bg-white hover:bg-yellow-50 focus:ring-yellow-500`}
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  Rejeitar
                </button>
              )}

              {selectedNode && canShowWorkflowActions(selectedNode) && (
                canArchiveWithWorkflow(selectedNode) ||
                shouldShowCoherenceBlockedWorkflowAction(
                  'Archive',
                  !!currentStepRuntime?.hasOpenStep && currentWorkflowUiStatus(selectedNode) === 'published'
                )) && (
                <button
                  type="button"
                  disabled={
                    !!workflowActionDisabledReason('Archive') || !canArchiveWithWorkflow(selectedNode)
                  }
                  title={workflowActionDisabledReason('Archive') ?? undefined}
                  onClick={() => handleWorkflowAction('Archive')}
                  className={`${workflowActionButtonClass(
                    !workflowActionDisabledReason('Archive') && !!canArchiveWithWorkflow(selectedNode)
                  )} border-amber-300 text-amber-800 bg-white hover:bg-amber-50 focus:ring-amber-500`}
                >
                  <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                  Arquivar
                </button>
              )}

              {selectedNode &&
                ((canShowWorkflowActions(selectedNode) && canSignWorkflow(selectedNode)) ||
                  canSignStandaloneSignature(selectedNode)) && (
                <button
                  type="button"
                  onClick={() => handleWorkflowSignClick()}
                  className="inline-flex items-center px-4 py-2 border border-indigo-300 rounded-md shadow-sm text-sm font-medium text-indigo-800 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Assinar
                </button>
              )}

              {selectedNode && canShowWorkflowActions(selectedNode) && (
                canCompleteEditWorkflow(selectedNode) ||
                shouldShowCoherenceBlockedWorkflowAction(
                  'CompleteEdit',
                  !!currentStepRuntime?.hasOpenStep && currentWorkflowUiStatus(selectedNode) === 'edit'
                )) && (
                <button
                  type="button"
                  disabled={
                    !!workflowActionDisabledReason('CompleteEdit') || !canCompleteEditWorkflow(selectedNode)
                  }
                  title={workflowActionDisabledReason('CompleteEdit') ?? undefined}
                  onClick={() => handleWorkflowAction('CompleteEdit')}
                  className={`${workflowActionButtonClass(
                    !workflowActionDisabledReason('CompleteEdit') && !!canCompleteEditWorkflow(selectedNode)
                  )} border-gray-300 text-gray-800 bg-white hover:bg-gray-50 focus:ring-gray-400`}
                >
                  <PencilSquareIcon className="h-4 w-4 mr-2" />
                  Avançar edição
                </button>
              )}

              {selectedNode && canShowWorkflowActions(selectedNode) && canForkToNewVersion(selectedNode) && (
                <button
                  type="button"
                  onClick={() => handleWorkflowAction('Edit')}
                  className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-800 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                  {statusForNode(selectedNode) === 'archived' ? 'Reativar' : 'Nova versão'}
                </button>
              )}
            </div>
          </div>
        ) : null}

        {/* Janela de colaboração suspensa */}
        <div className="fixed bottom-4 right-4">
          <DocumentCollaboration selectedNode={selectedNode} />
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      {renderNodeContent(selectedNode)}

      {/* Remove o modal de confirmação de exclusão duplicado */}
      {/* <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
          <div className="relative bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <Dialog.Title className="text-lg font-semibold mb-4">Confirmar exclusão</Dialog.Title>
            <p className="mb-6">Tem certeza que deseja excluir "{selectedNode?.title}"?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      </Dialog> */}

      {/* Modal de visualização */}
      <Dialog 
        open={showViewModal} 
        onClose={() => {
          // Executar função de limpeza se existir
          if (cleanupFunction) {
            cleanupFunction();
          } else {
            setShowViewModal(false);
          }
          // Resetar estados
          setViewContent('');
          setCleanupFunction(null);
          setIsLoading(false);
        }} 
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
          <div className="relative bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[80vh]">
            <div className="flex items-center justify-between p-4 border-b">
              <Dialog.Title className="text-lg font-semibold">Visualizar: {selectedNode?.title}</Dialog.Title>
              <button
                onClick={() => {
                  // Executar função de limpeza se existir
                  if (cleanupFunction) {
                    cleanupFunction();
                  } else {
                    setShowViewModal(false);
                  }
                  // Resetar estados
                  setViewContent('');
                  setCleanupFunction(null);
                  setIsLoading(false);
                }}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: viewContent }} />
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Modal de permissões */}
      {selectedNode && (
        <DocumentNodePermissionsModal
          isOpen={showPermissionsModal}
          onClose={() => setShowPermissionsModal(false)}
          nodeId={selectedNode.id}
          nodeName={selectedNode.title}
          nodePath={selectedNode.path}
        />
      )}

      <ShareDocumentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        documentId={selectedNode?.id ?? null}
        documentTitle={selectedNode?.title ?? ''}
        canEditSharing={permissions.canEdit}
      />

      <Dialog open={showRenameModal} onClose={() => !renameSubmitting && setShowRenameModal(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">Renomear</Dialog.Title>
            <label htmlFor="rename-document-title" className="sr-only">
              Novo nome
            </label>
            <input
              id="rename-document-title"
              type="text"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleConfirmRename();
                }
              }}
              disabled={renameSubmitting}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              autoFocus
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowRenameModal(false)}
                disabled={renameSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmRename()}
                disabled={renameSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {renameSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Modal de ação bloqueada */}
      <ActionBlockedModal
        isOpen={showActionBlockedModal}
        onClose={() => setShowActionBlockedModal(false)}
        actionName={blockedActionName}
      />

      {/* Modal de comentários */}
      {selectedNode && (
        <DocumentCommentsModal
          isOpen={commentsModalOpen}
          onClose={() => setCommentsModalOpen(false)}
          documentId={selectedNode.id}
          documentTitle={selectedNode.title}
        />
      )}

      {/* Document Viewer Modal */}
      {showDocumentViewer && selectedNode && (
        <DocumentViewer
          document={selectedNode}
          onClose={() => setShowDocumentViewer(false)}
          signatureRequestId={
            latestSignatureRequest &&
            documentIdsMatch(latestSignatureRequest.documentId, selectedNode.id) &&
            (latestSignatureRequest.finalSignedDocumentAvailable === true ||
              latestSignatureRequest.partialSignedDocumentAvailable === true ||
              signatureHasCollectedSignatures(latestSignatureRequest))
              ? latestSignatureRequest.id
              : undefined
          }
          preferSignedDocument={
            Boolean(
              latestSignatureRequest &&
                documentIdsMatch(latestSignatureRequest.documentId, selectedNode.id) &&
                signatureHasCollectedSignatures(latestSignatureRequest)
            )
          }
          partialSignedDocumentFailed={
            latestSignatureRequest?.partialSignedDocumentFailed === true
          }
        />
      )}

      {/* OpenDocument Viewer Modal */}
      {showOpenDocumentViewer && selectedNode && (
        <OpenDocumentViewer
          document={selectedNode}
          onClose={() => setShowOpenDocumentViewer(false)}
        />
      )}

      {showVersionHistoryModal && selectedNode && selectedNode.nodeType === 'edoc' && (
        <DocumentVersionHistoryModal
          isOpen={showVersionHistoryModal}
          onClose={() => setShowVersionHistoryModal(false)}
          documentId={selectedNode.id}
          documentTitle={selectedNode.title}
          onRestored={() => {
            onDocumentUpdated(selectedNode.id);
            refreshStatus();
          }}
        />
      )}

      {showDocumentWorkflowHistoryModal && selectedNode && selectedNode.id !== 'root' && (
        <DocumentWorkflowHistoryModal
          isOpen={showDocumentWorkflowHistoryModal}
          onClose={() => setShowDocumentWorkflowHistoryModal(false)}
          documentId={selectedNode.id}
          documentTitle={selectedNode.title}
        />
      )}

      {showDocumentAuditHistoryModal && selectedNode && selectedNode.id !== 'root' && (
        <DocumentAuditHistoryModal
          isOpen={showDocumentAuditHistoryModal}
          onClose={() => setShowDocumentAuditHistoryModal(false)}
          documentId={selectedNode.id}
          documentTitle={selectedNode.title}
        />
      )}

      {showPublishPreview && selectedNode && (
        <PublishPreviewModal
          open={showPublishPreview}
          document={selectedNode}
          onClose={() => setShowPublishPreview(false)}
          onConfirm={(publicationExpiresAt) => void handleConfirmPublish(publicationExpiresAt)}
          isPublishing={isPublishing}
          initialPublicationExpiresAt={publicationExpiresAt}
        />
      )}

      {showStandaloneSignatureRequestModal && selectedNode?.id && selectedNode.id !== 'root' && (
        <StandaloneSignatureRequestModal
          open={showStandaloneSignatureRequestModal}
          documentId={selectedNode.id}
          documentVersionId={documentCurrentVersionId}
          nodeType={selectedNode.nodeType}
          showExpiresAtField={applyStandaloneSignatureRules}
          onClose={() => setShowStandaloneSignatureRequestModal(false)}
          onCreated={async (signatureRequestId) => {
            await refreshDocumentSignatureState(selectedNode.id);
            onDocumentUpdated(selectedNode.id);
            
            if (selectedNode.nodeType === 'edoc' && signatureRequestId)
            {
              setShowStandaloneSignatureRequestModal(false);
              setPendingSignaturePlacementModal({
                signatureRequestId,
                action: 'standalone',
              });
            }
          }}
        />
      )}

      {showSignersModal && selectedNode?.id && selectedNode.id !== 'root' && (
        <DocumentSignersModal
          isOpen={showSignersModal}
          onClose={() => setShowSignersModal(false)}
          documentId={selectedNode.id}
          onChanged={() => refreshDocumentSignatureState(selectedNode.id)}
        />
      )}

      {pendingSignaturePlacementModal && selectedNode?.id && selectedNode.id !== 'root' && (
        <SignaturePlacementModal
          open={true}
          documentId={selectedNode.id}
          documentVersionId={documentCurrentVersionId}
          nodeType={selectedNode.nodeType as 'pdf' | 'edoc'}
          signatureRequestId={pendingSignaturePlacementModal.signatureRequestId}
          onClose={() => setPendingSignaturePlacementModal(null)}
          onConfirm={handleSignaturePlacementConfirm}
        />
      )}

      {workflowTransitionModal && selectedNode?.id && selectedNode.id !== 'root' && (
        <WorkflowTransitionModal
          isOpen
          onClose={() => setWorkflowTransitionModal(null)}
          documentId={selectedNode.id}
          action={workflowTransitionModal.action}
          requiresComment={workflowTransitionModal.requiresComment}
          onBeforeExecute={handleWorkflowTransitionBeforeExecute}
          onSuccess={result => void handleWorkflowTransitionSuccess(result)}
        />
      )}

      {workflowSignModalSigner && selectedNode?.id && selectedNode.id !== 'root' && user?.email ? (
        <WorkflowSignSignatureModal
          isOpen
          documentId={selectedNode.id}
          signer={workflowSignModalSigner}
          userEmail={user.email}
          inviteToken={signatureInviteToken}
          onClose={() => setWorkflowSignModalSigner(null)}
          onCompleted={() => void refreshAfterDigitalSignature()}
        />
      ) : null}

      {workflowNewVersionModal && selectedNode?.id && selectedNode.id !== 'root' && (
        <WorkflowNewVersionConfirmModal
          isOpen
          variant={workflowNewVersionModal}
          documentId={selectedNode.id}
          requiresComment={currentStepRuntime?.requiresComment === true}
          onClose={() => setWorkflowNewVersionModal(null)}
          onSuccess={result => {
            setWorkflowNewVersionModal(null);
            void handleWorkflowTransitionSuccess(result);
          }}
        />
      )}

      {showLifecycleScheduleModal && selectedNode?.id && selectedNode.id !== 'root' && (
        <DocumentLifecycleScheduleModal
          isOpen={showLifecycleScheduleModal}
          onClose={() => setShowLifecycleScheduleModal(false)}
          documentId={selectedNode.id}
          canSchedulePublish={canWorkflowPublish(selectedNode)}
          canScheduleArchive={canArchiveWithWorkflow(selectedNode)}
          onSuccess={(result: DocumentLifecycleScheduleDto) => {
            setLifecycleSchedule({
              publish: result.scheduledPublishUtc ?? null,
              archive: result.scheduledArchiveUtc ?? null,
            });
            if (result.coherenceWarning) {
              addToast(result.coherenceWarning, 'info', 6000);
            } else {
              addToast('Agendamento atualizado.', 'success');
            }
            onDocumentUpdated(selectedNode.id);
          }}
        />
      )}

      <AddToChatbotModal
        open={showAddToChatbotModal}
        documentId={selectedNode?.id ?? ''}
        alreadyIndexedWorkspaceIds={documentStatus?.indexedWorkspaceIds ?? []}
        onClose={() => setShowAddToChatbotModal(false)}
        onConfirm={handleAddToChatbotConfirm}
        isSubmitting={selectedNode ? chatbotLoadingDocuments.has(selectedNode.id) : false}
      />

      <RemoveFromChatbotModal
        open={showRemoveFromChatbotModal}
        documentId={selectedNode?.id ?? ''}
        indexedWorkspaceIds={documentStatus?.indexedWorkspaceIds ?? []}
        onClose={() => setShowRemoveFromChatbotModal(false)}
        onConfirm={handleRemoveFromChatbotConfirm}
        isSubmitting={selectedNode ? chatbotLoadingDocuments.has(selectedNode.id) : false}
      />

      <ManageChatbotsModal
        open={showManageChatbotsModal}
        documentId={selectedNode?.id ?? ''}
        currentlyIndexedWorkspaceIds={documentStatus?.indexedWorkspaceIds ?? []}
        onClose={() => setShowManageChatbotsModal(false)}
        onConfirm={handleManageChatbotsConfirm}
        isSubmitting={selectedNode ? chatbotLoadingDocuments.has(selectedNode.id) : false}
      />

      {/* Modal de documentos relacionados */}
      {selectedNode && (
        <RelatedDocumentsModal
          isOpen={showRelatedModal}
          onClose={() => setShowRelatedModal(false)}
          documentId={selectedNode.id}
          onRelationshipCreated={() => {
            documentRelationshipService
              .getByDocumentId(selectedNode.id)
              .then(setRelatedDocuments)
              .catch(() => setRelatedDocuments([]));
          }}
        />
      )}
    </div>
  );
};

export default DocumentContent;
